import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  MessageCircle,
  ShoppingBag,
  Undo2,
  AlertTriangle,
  Wallet,
  Lock,
  ChevronLeft,
  ChevronRight,
  Instagram,
  ArrowUpRight,
  Sparkles,
  Download,
  Search,
  Pencil,
  History,
  ChevronDown,
  FileSpreadsheet,
  FileText,
  Zap,
  Film,
  Info,
  BookOpen,
  Globe,
} from "lucide-react";
import api, { cachedGet, invalidateCache } from "@/lib/api";
import { formatMoney } from "@/lib/currency";

// A zero-value amount reads "Free" (free digital claims) instead of "₹0".
const formatMoneyOrFree = (amount, currency) =>
  Number(amount || 0) === 0 ? "Free" : formatMoney(amount, currency);
import { useMerchant, isOwner } from "@/lib/useMerchant";
import { connectToInstagram } from "@/lib/instagramOAuth";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { toast } from "sonner";
import DashboardNotifications from "@/components/DashboardNotifications";
import FeedbackPrompt from "@/components/FeedbackPrompt";
import CustomerEditDialog from "@/components/CustomerEditDialog";
import CustomerHistorySheet from "@/components/CustomerHistorySheet";
import AutomationsPanel from "@/components/AutomationsPanel";
import StoryAutomationsPanel from "@/components/StoryAutomationsPanel";
import ShipmentsPanel from "@/components/ShipmentsPanel";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

function igProfileUrl(handle) {
  if (!handle) return null;
  const clean = String(handle).replace(/^@/, "").trim();
  return clean ? `https://instagram.com/${clean}` : null;
}

// Guards against "Invalid Date" showing up for rows with a missing/malformed
// timestamp — returns null instead so callers can render "—".
function safeDate(value) {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

// Renders a timestamp as two stacked lines (date, then time) so long
// datetimes never cramp a table column — used anywhere an "Updated" /
// "Last update" style column appears.
function DateTimeCell({ value }) {
  const d = safeDate(value);
  if (!d) return <span className="text-ink/40">—</span>;
  return (
    <>
      <p>{d.toLocaleDateString()}</p>
      <p className="text-xs text-ink/45">{d.toLocaleTimeString()}</p>
    </>
  );
}

function ProfilePic({ src, className, iconSize = 14 }) {
  const [broken, setBroken] = useState(false);
  if (!src || broken) {
    return (
      <span className={`grid place-items-center rounded-full bg-sunset-gradient text-white ${className}`}>
        <Instagram size={iconSize} />
      </span>
    );
  }
  return (
    <img
      src={src}
      alt=""
      className={`rounded-full object-cover ${className}`}
      onError={() => setBroken(true)}
    />
  );
}

const ABANDON_REASON_LABELS = {
  discount_request: "Asked about a discount",
  shipping_waiver_request: "Asked for free/waived shipping",
  too_expensive: "Said it was too expensive",
  cod_request: "Asked about Cash on Delivery",
  size_unavailable: "Wanted a size that's out of stock",
};

const STATE_MEANINGS = {
  completed: "Order placed and confirmed.",
  order_placed: "Order placed and confirmed.",
  awaiting_payment: "Payment link sent — waiting for the customer to pay.",
  confirming_address: "Bot is collecting or confirming the delivery address.",
  awaiting_quantity: "Bot asked how many they want, waiting on a reply.",
  awaiting_add_more: "Bot asked if they want to add anything else to their cart — waiting on a reply.",
  manual_review: "Bot got stuck and handed the chat over to you.",
  new: "Customer just started chatting — no product picked yet.",
  awaiting_return_reason: "Customer is being asked why they want to return the item.",
  paid: "Payment received.",
  paid_manual_review: "Payment reported manually (UPI/bank transfer) and confirmed by you.",
  pending_payment: "Waiting on payment.",
  pending_manual_verification: "Customer says they paid manually (UPI/bank transfer) — needs your Confirm/Reject.",
  link_failed: "Razorpay payment link failed to generate — needs attention.",
  awaiting_manual_payment: "Waiting for the customer to complete a manual payment.",
  requested: "Customer requested a return — needs your decision.",
  approved: "Return approved.",
  rejected: "Order rejected because payment couldn't be verified.",
  return_rejected: "You declined this return request. The customer was notified, along with your return policy.",
  blocked_bot_loop: "The bot has paused due to a high volume of messages in a short time.",
  returned: "This order was fully returned and refunded.",
  partially_returned: "Some items from this order were returned and refunded — the rest of the order stands.",
  awaiting_ig_handle_optional: "Order placed and paid — bot asked for the customer's Instagram handle (optional, just to have on file) and is waiting on a reply.",
  awaiting_ig_handle_optional_retry: "Customer's reply didn't look like a valid handle — bot asked once more.",
  awaiting_ig_handle_optional_final: "Bot's last attempt at a valid handle before giving up and flagging it for your team.",
  awaiting_support_ig_handle: "Customer asked for a human and the bot doesn't have their handle on file — asking for it before handing the chat to you.",
  awaiting_product_selection: "Bot sent the product menu / search results and is waiting for the customer to pick one.",
  awaiting_size: "Bot asked the customer to confirm their size, waiting on a reply.",
  exchange_requested: "Customer asked to swap an item for a different size — needs your decision in the Returns tab.",
  exchanged: "An item on this order was swapped for a different size and the stock was updated.",
  exchange_rejected: "You declined an exchange request on this order. The customer was notified.",
  awaiting_name: "Bot asked for the customer's name, waiting on a reply.",
  awaiting_phone: "Bot asked for the customer's phone number, waiting on a reply.",
  awaiting_email: "Bot asked for the customer's email (digital orders only), waiting on a reply.",
  choosing_edit_fields: "Customer asked to edit their saved details and is picking which field to change.",
  awaiting_country: "Bot asked for the customer's country, waiting on a reply.",
  awaiting_state: "Bot asked for the customer's state, waiting on a reply.",
  awaiting_city: "Bot asked for the customer's city, waiting on a reply.",
  awaiting_pincode: "Bot asked for the customer's PIN/postal code, waiting on a reply.",
  awaiting_address_line: "Bot asked for the customer's street address, waiting on a reply.",
  awaiting_reuse_confirmation: "Customer has saved details on file — bot asked if they want to reuse them for this order.",
  awaiting_coupon_code: "Bot asked if the customer has a coupon code, waiting on a reply.",
  awaiting_category_selection: "Bot sent the category list and is waiting for the customer to pick one.",
  awaiting_return_order_selection: "Customer has more than one order eligible for return — bot asked which one.",
  awaiting_return_item_selection: "Bot asked which item(s) on the order the customer wants to return.",
  awaiting_return_quantity: "Bot asked how many units of the item the customer wants to return.",
  awaiting_exchange_order_selection: "Customer has more than one order eligible for exchange — bot asked which one.",
  awaiting_exchange_item_selection: "Bot asked which item on the order the customer wants to exchange.",
  awaiting_exchange_size_selection: "Bot showed available sizes for the exchange and is waiting for the customer to pick one.",
  awaiting_support_confirmation: "Customer asked for a human — bot is confirming before handing the chat over.",
  awaiting_support_reason: "Bot asked why the customer wants a human, waiting on a reply.",
};

const DELIVERY_STATUS_META = {
  delivered: { icon: "✓", label: "Delivered", pillClassName: "bg-mint text-ink", textClassName: "text-emerald-600" },
  rto: { icon: "↩", label: "Returned to origin", pillClassName: "bg-peach text-ink", textClassName: "text-amber-600" },
  failed: { icon: "⚠", label: "Delivery failed", pillClassName: "bg-hotpink/10 text-hotpink", textClassName: "text-hotpink" },
};

function StateBadge({ state, label }) {
  const [open, setOpen] = useState(false);
  const styles = {
    completed: "bg-mint text-ink",
    order_placed: "bg-mint text-ink",
    awaiting_payment: "bg-peach text-ink",
    confirming_address: "bg-lilac text-ink",
    awaiting_quantity: "bg-lilac text-ink",
    awaiting_add_more: "bg-lilac text-ink",
    manual_review: "bg-hotpink text-white",
    new: "bg-ink/10 text-ink",
    awaiting_return_reason: "bg-peach text-ink",
    paid: "bg-mint text-ink",
    paid_manual_review: "bg-mint text-ink",
    pending_payment: "bg-peach text-ink",
    pending_manual_verification: "bg-lilac text-ink",
    link_failed: "bg-hotpink text-white",
    awaiting_manual_payment: "bg-peach text-ink",
    requested: "bg-peach text-ink",
    approved: "bg-mint text-ink",
    rejected: "bg-hotpink text-white",
    return_rejected: "bg-hotpink text-white",
    blocked_bot_loop: "bg-hotpink text-white",
    returned: "bg-mint text-ink",
    partially_returned: "bg-peach text-ink",
    awaiting_ig_handle_optional: "bg-lilac text-ink",
    awaiting_ig_handle_optional_retry: "bg-lilac text-ink",
    awaiting_ig_handle_optional_final: "bg-lilac text-ink",
    awaiting_support_ig_handle: "bg-peach text-ink",
    awaiting_product_selection: "bg-lilac text-ink",
    awaiting_size: "bg-lilac text-ink",
    exchange_requested: "bg-[#4C1D95] text-white",
    exchanged: "bg-mint text-ink",
    exchange_rejected: "bg-hotpink text-white",
    awaiting_name: "bg-lilac text-ink",
    awaiting_phone: "bg-lilac text-ink",
    awaiting_email: "bg-lilac text-ink",
    choosing_edit_fields: "bg-lilac text-ink",
    awaiting_country: "bg-lilac text-ink",
    awaiting_state: "bg-lilac text-ink",
    awaiting_city: "bg-lilac text-ink",
    awaiting_pincode: "bg-lilac text-ink",
    awaiting_address_line: "bg-lilac text-ink",
    awaiting_reuse_confirmation: "bg-lilac text-ink",
    awaiting_coupon_code: "bg-lilac text-ink",
    awaiting_category_selection: "bg-lilac text-ink",
    awaiting_return_order_selection: "bg-peach text-ink",
    awaiting_return_item_selection: "bg-peach text-ink",
    awaiting_return_quantity: "bg-peach text-ink",
    awaiting_exchange_order_selection: "bg-lilac text-ink",
    awaiting_exchange_item_selection: "bg-lilac text-ink",
    awaiting_exchange_size_selection: "bg-lilac text-ink",
    awaiting_support_confirmation: "bg-peach text-ink",
    awaiting_support_reason: "bg-peach text-ink",
  };
  const meaning = STATE_MEANINGS[state] || "No description yet for this state.";
  return (
    <span className="group relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        title={meaning}
        className={`cursor-help rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.12em] ${styles[state] || "bg-ink/10 text-ink"}`}
      >
        {label || (state || "").replace(/_/g, " ")}
      </button>
      <span
        className={`absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink/10 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg ${
          open ? "block" : "hidden md:group-hover:block"
        }`}
      >
        {meaning}
        <Link
          to="/status-guide"
          onClick={(e) => e.stopPropagation()}
          className="mt-1.5 flex items-center gap-1 text-[10px] font-bold text-hotpink hover:underline"
        >
          <BookOpen size={10} /> See all statuses →
        </Link>
      </span>
    </span>
  );
}

// order_source is 'web' for real storefront checkouts (see
// api-public-cart-checkout); everything else (NULL or 'instagram') is the
// original Instagram DM flow.
function OrderSourceBadge({ source }) {
  const isWeb = source === "web";
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${
        isWeb ? "bg-sky-100 text-sky-700" : "bg-hotpink/10 text-hotpink"
      }`}
      title={isWeb ? "Placed on your storefront" : "Placed via Instagram DM"}
    >
      {isWeb ? <Globe size={9} /> : <Instagram size={9} />} {isWeb ? "Storefront" : "Instagram"}
    </span>
  );
}

function StatCard({ label, value, icon: Icon, accent = "bg-white", tip }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`relative rounded-3xl border border-ink/10 p-5 ${accent}`} data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-ink/60">{label}</p>
          {tip && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
              aria-label={`What does ${label} mean?`}
              className="grid h-4 w-4 shrink-0 place-items-center rounded-full text-ink/40 hover:text-ink"
            >
              <Info size={13} />
            </button>
          )}
        </div>
        <span className="grid h-8 w-8 place-items-center rounded-xl bg-white/70 text-ink shadow-sm">
          <Icon size={16} strokeWidth={2.4} />
        </span>
      </div>
      <p className="mt-3 font-display text-3xl font-black tracking-tighter">{value}</p>
      {open && (
        <div className="absolute left-3 right-3 top-full z-20 mt-1.5 rounded-xl border border-ink/10 bg-white p-3 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg">
          {tip}
        </div>
      )}
    </div>
  );
}

function Pager({ page, total, limit, onPage, testPrefix }) {
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="mt-5 flex items-center justify-between border-t border-ink/10 pt-4 text-sm text-ink/70">
      <span data-testid={`${testPrefix}-page-info`}>
        Page <span className="font-semibold text-ink">{page}</span> of {pages} · {total} total
      </span>
      <div className="flex items-center gap-2">
        <button
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
          data-testid={`${testPrefix}-prev`}
          className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
        >
          <ChevronLeft size={14} /> Prev
        </button>
        <button
          disabled={page >= pages}
          onClick={() => onPage(page + 1)}
          data-testid={`${testPrefix}-next`}
          className="inline-flex items-center gap-1 rounded-full border border-ink/10 bg-white px-3 py-1.5 disabled:opacity-40"
        >
          Next <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
}

const FREE_PLAN_ROW_CAP = 15;

function FreePlanRowCap({ shown, total, label }) {
  const hasMore = total > shown;
  return (
    <div className="mt-5 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-5">
      <div className="flex items-center gap-3">
        <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
          <Lock size={18} />
        </span>
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Free plan</p>
          <p className="mt-0.5 text-sm text-ink/70">
            Showing your {shown} most recent {label}
            {hasMore ? (
              <> — <strong className="text-ink">{total - shown} more</strong> hidden on Free.</>
            ) : (
              "."
            )}
          </p>
        </div>
      </div>
      <a
        href="/plans"
        className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-center text-sm font-semibold text-white"
      >
        <span>Upgrade to Starter to see all</span> <ArrowUpRight size={14} className="flex-none" />
      </a>
    </div>
  );
}

function FreeDeliveryUpsell({ className = "" }) {
  return (
    <a
      href="/plans"
      className={`mt-1 flex items-center gap-1 text-[10px] font-semibold text-hotpink hover:underline ${className}`}
    >
      <Lock size={10} className="flex-none" /> Upgrade to Starter to see live delivery status
    </a>
  );
}

function ReturnItemsToggle({ items, currency }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="text-[10px] font-semibold text-hotpink underline decoration-dotted"
      >
        {open ? "Hide items" : `View items (${items.length})`}
      </button>
      {open && (
        <ul className="mt-1 space-y-1 text-[11px] text-ink/60">
          {items.map((it, i) => (
            <li key={i} className="flex items-start justify-between gap-3">
              <span>
                {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                {it.ordered_quantity && it.ordered_quantity !== it.quantity && (
                  <span className="text-ink/40"> (of {it.ordered_quantity} ordered)</span>
                )}
                {Number(it.quantity || 1) > 1 && (
                  <span className="block text-[10px] text-ink/40">{formatMoneyOrFree(it.unit_price, currency)} each</span>
                )}
              </span>
              <span className="flex-none font-mono">
                {formatMoneyOrFree(Number(it.unit_price || 0) * Number(it.quantity || 1), currency)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function OrderAmountBreakdown({ o, currency }) {
  const [open, setOpen] = useState(false);
  const discountAmount = Number(o.discount_amount || 0);
  const shippingFee = Number(o.shipping_fee_charged || 0);
  const hasDiscount = discountAmount > 0;
  const hasShipping = shippingFee > 0;
  const items = o.items || [];
  const hasItemDetail = items.length > 0;
  const hasBreakdown = hasDiscount || hasShipping || hasItemDetail;
  // Derived, since the API doesn't return a subtotal field:
  const subtotal = Number(o.total_amount || 0) + discountAmount - shippingFee;

  return (
    <div className="relative">
      <p className="font-semibold">{formatMoneyOrFree(o.total_amount, currency)}</p>
      {hasBreakdown && (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid={`order-breakup-toggle-${o.id}`}
            className="mt-0.5 whitespace-nowrap text-[10px] font-semibold text-hotpink underline decoration-dotted"
          >
            {open ? "Hide breakup" : "View breakup"}
          </button>
          {open && (
            <div className="absolute left-0 top-full z-20 mt-1.5 w-64 space-y-1 rounded-lg border border-ink/10 bg-white px-2.5 py-2 text-[11px] text-ink/70 shadow-lg">
              {hasItemDetail && (
                <>
                  {items.map((it, idx) => (
                    <div key={it.id ?? idx} className="flex items-start justify-between gap-4">
                      <span className="max-w-[160px]">
                        {it.product_name}
                        {it.size ? ` · ${it.size}` : ""} × {it.quantity}
                        {Number(it.quantity || 1) > 1 && (
                          <span className="block text-[10px] text-ink/40">
                            {formatMoneyOrFree(it.unit_price, currency)} each
                          </span>
                        )}
                      </span>
                      <span className="flex-none">
                        {formatMoneyOrFree(Number(it.unit_price || 0) * Number(it.quantity || 1), currency)}
                      </span>
                    </div>
                  ))}
                  <div className="border-t border-ink/10 pt-1" />
                </>
              )}
              <div className="flex items-center justify-between gap-4">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal, currency)}</span>
              </div>
              {hasDiscount && (
                <div className="flex items-center justify-between gap-4 text-hotpink">
                  <span>Discount{o.discount_code ? ` (${o.discount_code})` : ""}</span>
                  <span>−{formatMoney(discountAmount, currency)}</span>
                </div>
              )}
              {hasShipping && (
                <div className="flex items-center justify-between gap-4">
                  <span>Shipping</span>
                  <span>+{formatMoney(shippingFee, currency)}</span>
                </div>
              )}
              <div className="flex items-center justify-between gap-4 border-t border-ink/10 pt-1 font-semibold text-ink">
                <span>Total</span>
                <span>{formatMoneyOrFree(o.total_amount, currency)}</span>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function ListToolbar({ search, onSearchChange, onSearchSubmit, exportSearch, exportPath, exportLabel, plan }) {
  const exportLocked = !(plan === "growth" || plan === "pro");
  // Fetches the export as a blob via the normal `api` instance (which
  // attaches the session JWT as an Authorization header, same as every
  // other request) instead of building a download URL with the token as
  // a `?token=` query param. That query-param approach put the full
  // session token somewhere it didn't need to be -- proxy/access logs,
  // browser history, and any Referer header a download page might send.
  // A blob: URL never touches the network, so the token never leaves the
  // Authorization header of one authenticated XHR.
  const download = async (format) => {
    try {
      const q = exportSearch ? `?search=${encodeURIComponent(exportSearch)}` : "";
      const res = await api.get(`${exportPath}/${format}${q}`, { responseType: "blob" });
      const disposition = res.headers?.["content-disposition"] || "";
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const filename = match ? match[1] : `${exportLabel}.${format}`;
      const blobUrl = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = filename;
      a.rel = "noopener";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(blobUrl);
      toast.success(`Downloading ${exportLabel} ${format.toUpperCase()}…`);
    } catch (e) {
      toast.error(`Couldn't download ${exportLabel} ${format.toUpperCase()} — try again.`);
    }
  };

  return (
    <div className="flex items-center gap-2 md:flex-wrap md:gap-3">
      <form onSubmit={onSearchSubmit} className="flex min-w-0 flex-1 items-center gap-2 md:flex-initial">
        <div className="relative min-w-0 flex-1 md:flex-initial">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search name, phone, order #…"
            data-testid={`${exportLabel}-search-input`}
            className="w-full rounded-full border border-ink/15 bg-white py-2 pl-9 pr-3 text-sm focus:border-hotpink focus:outline-none md:w-64"
          />
        </div>
        <button
          type="submit"
          data-testid={`${exportLabel}-search-btn`}
          className="shrink-0 rounded-full bg-ink px-3 py-2 text-sm font-semibold text-cream hover:bg-ink/85 md:px-4"
        >
          Search
        </button>
      </form>
      {exportLocked ? (
        <a
          href="/plans"
          data-testid={`${exportLabel}-export-locked`}
          title="Export is available on Growth and Pro plans"
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-ink/15 bg-white px-3 py-2 text-sm font-semibold text-ink/50 md:gap-2 md:px-5"
        >
          <Lock size={14} /> <span className="hidden sm:inline">Export</span>
        </a>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid={`${exportLabel}-export-menu`}
              className="cta-glow inline-flex shrink-0 items-center gap-1.5 rounded-full bg-sunset-gradient px-3 py-2 text-sm font-semibold text-white md:gap-2 md:px-5"
            >
              <Download size={14} /> <span className="hidden sm:inline">Export</span> <ChevronDown size={14} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-white">
            <DropdownMenuItem onClick={() => download("csv")} data-testid={`${exportLabel}-export-csv`} className="cursor-pointer">
              <FileText size={14} className="mr-2" /> Download CSV
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => download("xlsx")} data-testid={`${exportLabel}-export-xlsx`} className="cursor-pointer">
              <FileSpreadsheet size={14} className="mr-2" /> Download Excel (.xlsx)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function CustomersPanel({ merchantReady, currency, plan }) {
  const isFree = plan === "free";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyId, setHistoryId] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);

  useEffect(() => {
    if (!merchantReady) return;
    setLoading(true);
    const q = query ? `&search=${encodeURIComponent(query)}` : "";
    const effectivePage = isFree ? 1 : page;
    const effectiveLimit = isFree ? FREE_PLAN_ROW_CAP : 20;
    cachedGet(`/merchant/customers?page=${effectivePage}&limit=${effectiveLimit}${q}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page, query, merchantReady, reloadTick, isFree]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const onEdit = (c) => {
    setEditing(c);
    setEditOpen(true);
  };

  const onHistory = (c) => {
    setHistoryId(c.customer_ig_id);
    setHistoryOpen(true);
  };

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="customers-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Customers</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            Every buyer who has ever DM&apos;d you
          </h2>
          <p className="mt-1 text-sm text-ink/60">
            Name, phone, email &amp; delivery address — click a row to edit or view history.
          </p>
        </div>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={applySearch}
          exportSearch={query}
          exportPath="/merchant/export"
          exportLabel="customers"
          plan={plan}
        />
      </div>

      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[980px] table-fixed text-sm">
          <colgroup>
            <col className="w-[18%]" />
            <col className="w-[10%]" />
            <col className="w-[16%]" />
            <col className="w-[24%]" />
            <col className="w-[6%]" />
            <col className="w-[8%]" />
            <col className="w-[8%]" />
            <col className="w-[10%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Phone</th>
              <th className="pb-3 pr-4">Email</th>
              <th className="pb-3 pr-4">Address</th>
              <th className="pb-3 pr-4">Orders</th>
              <th className="pb-3 pr-4">Spent</th>
              <th className="pb-3 pr-4">First Seen</th>
              <th className="pb-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={8} className="py-6 text-center text-ink/50">No customers found.</td></tr>
            ) : (
              data.items.map((c) => (
                <tr key={c.customer_ig_id} className="border-b border-ink/5" data-testid={`customer-row-${c.customer_ig_id}`}>
                  <td className="py-3">
                    <div className="flex items-center gap-2">
                      <ProfilePic src={c.profile_pic} className="h-8 w-8" iconSize={14} />
                      <div>
                        {c.instagram_profile_url ? (
                          <a
                            href={c.instagram_profile_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`customer-ig-link-${c.customer_ig_id}`}
                            className="font-semibold text-hotpink hover:underline"
                            title={`Open @${c.customer_ig_handle} on Instagram`}
                          >
                            {c.display_name}
                          </a>
                        ) : (
                          <p className="font-semibold">{c.display_name}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 font-mono text-xs">{c.customer_phone || "—"}</td>
                  <td className="py-3 text-ink/70">{c.customer_email || "—"}</td>
                  <td className="py-3 align-top text-ink/70">
                    <p className="text-xs leading-relaxed">{c.address_str || "—"}</p>
                  </td>
                  <td className="py-3 font-semibold">{c.order_count || 0}</td>
                  <td className="py-3 font-semibold">{formatMoney(c.total_spent, currency)}</td>
                  <td className="py-3 text-ink/60">{c.first_seen ? new Date(c.first_seen).toLocaleDateString() : "—"}</td>
                  <td className="py-3">
                    <div className="flex flex-col items-end gap-3">
                      <button
                        onClick={() => onHistory(c)}
                        data-testid={`customer-history-${c.customer_ig_id}`}
                        title="View history"
                        className="inline-flex w-full items-center justify-center gap-1 rounded-full border border-ink/15 bg-white px-2.5 py-1 text-xs font-semibold hover:bg-ink/5"
                      >
                        <History size={12} /> History
                      </button>
                      <button
                        onClick={() => onEdit(c)}
                        data-testid={`customer-edit-${c.customer_ig_id}`}
                        title="Edit"
                        className="inline-flex w-full items-center justify-center gap-1 rounded-full bg-ink px-2.5 py-1 text-xs font-semibold text-cream hover:bg-ink/85"
                      >
                        <Pencil size={12} /> Edit
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div className="mt-6 space-y-3 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">No customers found.</p>
        ) : (
          data.items.map((c) => (
            <div
              key={c.customer_ig_id}
              className="rounded-2xl border border-ink/10 p-4"
              data-testid={`customer-card-${c.customer_ig_id}`}
            >
              <div className="flex items-center gap-3">
                <ProfilePic src={c.profile_pic} className="h-10 w-10" iconSize={16} />
                <div className="min-w-0 flex-1">
                  {c.instagram_profile_url ? (
                    <a
                      href={c.instagram_profile_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="truncate font-semibold text-hotpink hover:underline block"
                      title={`Open @${c.customer_ig_handle} on Instagram`}
                    >
                      {c.display_name}
                    </a>
                  ) : (
                    <p className="truncate font-semibold">{c.display_name}</p>
                  )}
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Spent</p>
                  <p className="text-xs font-semibold text-ink/70">{formatMoney(c.total_spent, currency)}</p>
                </div>
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs text-ink/70">
                <dt className="text-ink/40">Phone</dt><dd className="text-right font-mono">{c.customer_phone || "—"}</dd>
                <dt className="text-ink/40">Email</dt><dd className="truncate text-right">{c.customer_email || "—"}</dd>
                <dt className="text-ink/40">Orders</dt><dd className="text-right">{c.order_count || 0}</dd>
                <dt className="text-ink/40">First seen</dt><dd className="text-right">{c.first_seen ? new Date(c.first_seen).toLocaleDateString() : "—"}</dd>
              </dl>
              {c.address_str && (
                <div className="mt-2">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Address</p>
                  <p className="text-xs text-ink/60">{c.address_str}</p>
                </div>
              )}
              <div className="mt-3 flex gap-2">
                <button onClick={() => onHistory(c)} className="flex-1 rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold">
                  <History size={12} className="mr-1 inline" /> History
                </button>
                <button onClick={() => onEdit(c)} className="flex-1 rounded-full bg-ink px-3 py-1.5 text-xs font-semibold text-cream">
                  <Pencil size={12} className="mr-1 inline" /> Edit
                </button>
              </div>
            </div>
          ))
        )}
      </div>
      {isFree ? (
        <FreePlanRowCap shown={data.items.length} total={data.total} label="customers" />
      ) : (
        <Pager page={page} total={data.total} limit={data.limit} onPage={setPage} testPrefix="customers" />
      )}

      <CustomerEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        customer={editing}
        onSaved={() => setReloadTick((t) => t + 1)}
      />
      <CustomerHistorySheet
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        customerIgId={historyId}
        currency={currency}
        plan={plan}
      />
    </div>
  );
}

function ChatsPanel({ merchantReady, plan }) {
  const isFree = plan === "free";
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!merchantReady) return;
    setLoading(true);
    const effectivePage = isFree ? 1 : page;
    const effectiveLimit = isFree ? FREE_PLAN_ROW_CAP : 20;
    cachedGet(`/merchant/chats?page=${effectivePage}&limit=${effectiveLimit}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [page, merchantReady, isFree]);

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="chats-panel">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Instagram Chats</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">
            Every DM conversation
          </h2>
        </div>
      </div>

      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[760px] table-fixed text-sm">
          <colgroup>
            <col className="w-[26%]" />
            <col className="w-[30%]" />
            <col className="w-[16%]" />
            <col className="w-[12%]" />
            <col className="w-[16%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Cart</th>
              <th className="pb-3 pr-4">State</th>
              <th className="pb-3 pr-4">Started</th>
              <th className="pb-3">Updated</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={5} className="py-6 text-center text-ink/50">No chats yet.</td></tr>
            ) : (
              data.items.map((c) => (
                <tr key={c.id} className="border-b border-ink/5" data-testid={`chat-row-${c.id}`}>
                  <td className="py-3 pr-4 align-top">
                    <div className="flex items-center gap-2">
                      <ProfilePic src={c.profile_pic} className="h-8 w-8 flex-none" iconSize={14} />
                      <div className="min-w-0">
                        {c.customer_ig_handle ? (
                          <a href={igProfileUrl(c.customer_ig_handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Open @${c.customer_ig_handle} on Instagram`}
                            className="block truncate font-semibold text-hotpink hover:underline"
                          >
                            {c.display_name || c.customer_name || "—"}
                          </a>
                        ) : (
                          <p className="truncate font-semibold">{c.display_name || c.customer_name || "—"}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/70">
                    <p className="text-xs leading-relaxed break-words">
                      {c.cart && c.cart.length
                        ? c.cart.map((i) => `${i.name} × ${i.quantity || 1}`).join(", ")
                        : "—"}
                    </p>
                  </td>
                  <td className="py-3 pr-4 align-top"><StateBadge state={c.state} /></td>
                  <td className="py-3 pr-4 align-top text-ink/60">
                    {safeDate(c.created_at)?.toLocaleDateString() || "—"}
                  </td>
                  <td className="py-3 align-top text-ink/60">
                    <DateTimeCell value={c.updated_at} />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Phone layout: one card per chat, no sideways scrolling */}
      <div className="mt-6 space-y-3 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">No chats yet.</p>
        ) : (
          data.items.map((c) => (
            <div key={c.id} className="rounded-2xl border border-ink/10 p-4" data-testid={`chat-card-${c.id}`}>
              <div className="flex items-center gap-3">
                <ProfilePic src={c.profile_pic} className="h-10 w-10 flex-none" iconSize={16} />
                <div className="min-w-0 flex-1">
                  {c.customer_ig_handle ? (
                    <a
                      href={igProfileUrl(c.customer_ig_handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block truncate font-semibold text-hotpink hover:underline"
                    >
                      {c.display_name || c.customer_name || "—"}
                    </a>
                  ) : (
                    <p className="truncate font-semibold">{c.display_name || c.customer_name || "—"}</p>
                  )}
                </div>
                <StateBadge state={c.state} />
              </div>
              <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-xs text-ink/70">
                <dt className="text-ink/40">Cart</dt>
                <dd className="break-words text-right">
                  {c.cart && c.cart.length
                    ? c.cart.map((i) => `${i.name} × ${i.quantity || 1}`).join(", ")
                    : "—"}
                </dd>
                <dt className="text-ink/40">Started</dt>
                <dd className="text-right">{safeDate(c.created_at)?.toLocaleDateString() || "—"}</dd>
                <dt className="text-ink/40">Updated</dt>
                <dd className="text-right"><DateTimeCell value={c.updated_at} /></dd>
              </dl>
            </div>
          ))
        )}
      </div>

      {isFree ? (
        <FreePlanRowCap shown={data.items.length} total={data.total} label="chats" />
      ) : (
        <Pager page={page} total={data.total} limit={data.limit} onPage={setPage} testPrefix="chats" />
      )}
    </div>
  );
}

const RETURN_ITEM_STATUS_LABELS = {
  requested: "Return requested",
  approved: "Returned",
  rejected: "Return rejected",
};

// Builds an item's chronological status trail (e.g. "Delivered" then
// "Returned"). Delivery status lives at the order/shipment level (one
// shipment can carry several items) while return status is tracked per
// item, so an approved return never overwrites or hides the delivery
// event -- both get pushed onto the same trail if both happened.
function buildItemHistory(item, order) {
  const events = [];
  const deliveryMeta = order.delivery_status && DELIVERY_STATUS_META[order.delivery_status];

  // order.delivery_status is shipment-level, so on its own it can't tell us
  // whether THIS item was still in the shipment when it was delivered — an
  // item can be returned/refunded before the courier ever reaches the
  // customer. Only trust the delivery label for this item if delivery
  // actually happened before the return was decided (or there's nothing to
  // compare, which keeps existing behavior for the normal case).
  const returnDecidedAt = item.return_request_status === "approved" ? item.return_decided_at : null;
  const deliveredBeforeReturn =
    !returnDecidedAt ||
    !order.delivery_status_updated_at ||
    new Date(order.delivery_status_updated_at) <= new Date(returnDecidedAt);

  if (order.shipped_at && deliveryMeta && deliveredBeforeReturn) {
    events.push({
      key: "delivery",
      label: deliveryMeta.label,
      icon: deliveryMeta.icon,
      textClassName: deliveryMeta.textClassName,
      at: order.delivery_status_updated_at,
    });
  }
  if (item.return_request_status) {
    const isApproved = item.return_request_status === "approved";
    const isRejected = item.return_request_status === "rejected";
    events.push({
      key: "return",
      label: (RETURN_ITEM_STATUS_LABELS[item.return_request_status] || item.return_request_status) +
      (isApproved && item.returned_quantity && item.returned_quantity < item.quantity ? ` (${item.returned_quantity} of ${item.quantity})` : ""),
      icon: isApproved ? "↩" : isRejected ? "✕" : "•",
      textClassName: isApproved ? "text-emerald-600" : isRejected ? "text-hotpink" : "text-amber-600",
      at: isApproved ? item.return_decided_at : null,
    });
  }
  if (item.exchange_request_status) {
    const st = item.exchange_request_status;
    const sizes = item.exchange_to_size
      ? ` (${item.exchange_from_size ? item.exchange_from_size + " → " : "→ "}${item.exchange_to_size})`
      : "";
    events.push({
      key: "exchange",
      label: (st === "approved" ? "Exchanged" : st === "rejected" ? "Exchange rejected" : "Exchange requested") + sizes,
      icon: "⇄",
      textClassName: st === "approved" ? "text-emerald-600" : st === "rejected" ? "text-hotpink" : "text-[#4C1D95]",
      at: st === "requested" ? item.exchange_requested_at : item.exchange_decided_at,
    });
  }
  return events;
}

// An item can go through Return unless it already has a return decided on
// it, or it has an exchange request that's pending or already approved —
// once an exchange is in play, its fate is decided in the Returns tab, not
// re-litigated here. A rejected exchange doesn't block a return.
function isItemReturnable(item) {
  if (item.return_request_status) return false;
  if (item.exchange_request_status && item.exchange_request_status !== "rejected") return false;
  return true;
}

function ItemHistoryTrail({ events }) {
  if (!events || events.length === 0) return null;
  return (
    <span className="flex-none text-right text-[10px] font-semibold uppercase tracking-wide">
      {events.map((e, i) => (
        <span key={e.key} className="block">
          {i > 0 && <span className="mr-1 text-ink/30">→</span>}
          <span className={e.textClassName}>{e.icon} {e.label}</span>
          {e.at && (
            <span className="ml-1 font-normal normal-case text-ink/40">
              ({safeDate(e.at)?.toLocaleDateString() || "—"})
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

// Derives a human-readable payment method purely from fields the orders
// endpoint already returns (status + whether a manual-payment screenshot
// was attached) -- no new column, no extra query. The bot only ever routes
// an order down one of two rails: Razorpay's payment link, or a manual
// UPI/bank transfer the customer self-reports and the merchant verifies.
function paymentMethodLabel(o) {
  const manualStatuses = ["paid_manual_review", "pending_manual_verification", "awaiting_manual_payment", "pending_payment"];
  const razorpayStatuses = ["paid", "awaiting_payment", "link_failed"];
  if (manualStatuses.includes(o.status) || o.payment_screenshot_url) return "Manual — UPI / bank transfer";
  if (razorpayStatuses.includes(o.status)) return "Razorpay (online payment)";
  return null;
}

// Single source of truth for when a return window actually opens.
//   - Digital orders: immediately, at purchase (created_at).
//   - Physical + Shiprocket-tracked: the CONFIRMED delivery date -- never
//     estimated, since Shiprocket gives us a real signal for it. Returns
//     null (window not started) until delivery_status flips to "delivered".
//   - Physical + independent/3rd-party courier (no shiprocket_shipment_id,
//     so we'll never get a delivery confirmation): a fixed 2 days after
//     "Mark as shipped", per policy.
//   - Not shipped yet: null (window not started).
function getReturnWindowStart(order) {
  const isDigital = order.is_digital_order != null
    ? order.is_digital_order
    : Array.isArray(order.items) && order.items.length > 0 && order.items.every((i) => i.is_digital);
  if (isDigital) return new Date(order.created_at);
  if (order.shiprocket_shipment_id || order.shippo_tracking_code) {
    // Delivered but no timestamp (e.g. status set by hand in the DB) -> fall back to shipped_at
    const deliveredAt = order.delivery_status_updated_at || order.shipped_at;
    return order.delivery_status === "delivered" && deliveredAt ? new Date(deliveredAt) : null;
  }
  return order.shipped_at ? new Date(new Date(order.shipped_at).getTime() + 2 * 86400000) : null;
}

function formatReturnWindowStatus(order, windowDays) {
  const start = getReturnWindowStart(order);
  const now = Date.now();
  // start can be non-null but still in the FUTURE for the independent-
  // courier branch (shipped_at + 2 days computed up front) -- treat that
  // the same as "not started" instead of only checking for null, or the
  // open/closed math below runs on a negative days-since and prints
  // nonsense day counts.
  if (!start || start.getTime() > now) {
    if (!order.shipped_at) return "Return window hasn't started yet — order not shipped.";
    if (order.shiprocket_shipment_id || order.shippo_tracking_code) {
      return "Return window hasn't started yet — waiting for courier delivery confirmation.";
    }
    const days = Math.max(1, Math.ceil((start.getTime() - now) / 86400000));
    return `Return window opens in ${days} day${days === 1 ? "" : "s"} (2 days after shipping).`;
  }
  const daysSince = (now - start.getTime()) / 86400000;
  return daysSince > windowDays
    ? `Return window closed ${Math.floor(daysSince - windowDays)} day${Math.floor(daysSince - windowDays) === 1 ? "" : "s"} ago.`
    : `Return window open — ${Math.ceil(windowDays - daysSince)} day${Math.ceil(windowDays - daysSince) === 1 ? "" : "s"} left.`;
}

// Consolidated, read-mostly view of a single order — replaces the old
// scattered set of inline toggles (amount breakup, item list, return
// status, "view items") that used to live spread across the Orders table.
// Clicking an order number opens this instead of hunting through columns.
// All data here already lives on the order object the list endpoint
// returns, so this never makes its own network request.
function OrderDetailSheet({
  order,
  open,
  onClose,
  currency,
  returnWindowDays,
  showDiscount,
  showDeliveryTracking,
  isDigitalOrder,
  onConfirm,
  onReject,
  onShip,
  onLogReturn,
  isSubmitting,
  courierConnected,
  hideReturns,
}) {
  if (!open || !order) return null;

  const items = order.items || [];
  const discountAmount = Number(order.discount_amount || 0);
  const shippingFee = Number(order.shipping_fee_charged || 0);
  const subtotal = Number(order.total_amount || 0) + discountAmount - shippingFee;
  const digital = isDigitalOrder(order);
  const isPaidLike = order.status === "paid" || order.status === "paid_manual_review";
  const deliveryMeta = showDeliveryTracking && order.delivery_status && DELIVERY_STATUS_META[order.delivery_status];
  const windowDays = returnWindowDays ?? 7;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4" data-testid="order-detail-sheet">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-3xl bg-white p-6 md:p-7">
        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Order</p>
            <h3 className="mt-1 font-mono text-lg font-black tracking-tight" data-testid="order-detail-number">
              {order.order_number || String(order.id ?? "").slice(0, 8)}
            </h3>
            <div className="mt-1 text-xs text-ink/50"><DateTimeCell value={order.created_at} /></div>
          </div>
          <button
            onClick={onClose}
            data-testid="order-detail-close"
            className="rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold hover:bg-ink/5"
          >
            Close
          </button>
        </div>

        {/* Status row */}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <OrderSourceBadge source={order.order_source} />
          <StateBadge state={order.status} />
          {isPaidLike && order.return_status && <StateBadge state={order.return_status} />}
          {isPaidLike && order.exchange_status && <StateBadge state={order.exchange_status} />}
        </div>

        {/* Customer */}
        <div className="mt-5 rounded-2xl border border-ink/10 p-3">
          <div className="flex items-center gap-3">
            <ProfilePic src={order.profile_pic} className="h-10 w-10 flex-none" iconSize={16} />
            <div className="min-w-0">
              {order.customer_ig_handle ? (
                <a
                  href={igProfileUrl(order.customer_ig_handle)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="truncate font-semibold text-hotpink hover:underline block"
                >
                  {order.display_name || order.customer_name || "—"}
                </a>
              ) : (
                <p className="truncate font-semibold">{order.display_name || order.customer_name || "—"}</p>
              )}
              <p className="text-xs text-ink/50">Customer</p>
            </div>
          </div>
          {(order.customer_phone || order.customer_email || order.address_str) && (
            <dl className="mt-3 space-y-1 border-t border-ink/10 pt-2.5 text-xs text-ink/70">
              {order.customer_phone && (
                <div className="flex gap-2">
                  <dt className="flex-none font-semibold text-ink/40">Phone</dt>
                  <dd className="font-mono">{order.customer_phone}</dd>
                </div>
              )}
              {order.customer_email && (
                <div className="flex gap-2">
                  <dt className="flex-none font-semibold text-ink/40">Email</dt>
                  <dd className="truncate">{order.customer_email}</dd>
                </div>
              )}
              {order.address_str && (
                <div className="flex gap-2">
                  <dt className="flex-none font-semibold text-ink/40">Address</dt>
                  <dd className="leading-relaxed">{order.address_str}</dd>
                </div>
              )}
            </dl>
          )}
        </div>

        {/* Items ordered */}
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Items ordered</p>
          <div className="mt-2 space-y-2">
            {items.length === 0 ? (
              <p className="text-sm text-ink/50">No item detail on this order.</p>
            ) : (
              items.map((it, idx) => (
                <div key={it.id ?? idx} className="flex items-start justify-between gap-3 rounded-xl bg-ink/[0.03] px-3 py-2 text-sm">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink">
                      {it.product_name}{it.size ? ` · ${it.size}` : ""}
                      {it.is_digital && (
                        <span className="ml-1.5 rounded-full bg-lilac px-1.5 py-0.5 align-middle text-[9px] font-bold uppercase tracking-wide text-ink">
                          Digital
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-ink/50">
                      Qty {it.quantity} × {formatMoneyOrFree(it.unit_price, currency)}
                    </p>
                    <div className="mt-1">
                      <ItemHistoryTrail events={buildItemHistory(it, order)} />
                    </div>
                  </div>
                  <p className="flex-none font-semibold text-ink">
                    {formatMoneyOrFree(Number(it.unit_price || 0) * Number(it.quantity || 1), currency)}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Amount breakdown */}
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Amount</p>
          <div className="mt-2 space-y-1 rounded-xl bg-ink/[0.03] px-3 py-2.5 text-sm text-ink/70">
            <div className="flex items-center justify-between">
              <span>Subtotal</span>
              <span>{formatMoney(subtotal, currency)}</span>
            </div>
            {discountAmount > 0 && (
              <div className="flex items-center justify-between text-hotpink">
                <span>Discount{order.discount_code ? ` (${order.discount_code})` : ""}</span>
                <span>−{formatMoney(discountAmount, currency)}</span>
              </div>
            )}
            {shippingFee > 0 && (
              <div className="flex items-center justify-between">
                <span>Shipping</span>
                <span>+{formatMoney(shippingFee, currency)}</span>
              </div>
            )}
            <div className="flex items-center justify-between border-t border-ink/10 pt-1 font-semibold text-ink">
              <span>Total</span>
              <span>{formatMoneyOrFree(order.total_amount, currency)}</span>
            </div>
            {showDiscount && !order.discount_code && order.discount_kind && (
              <p className="pt-1 text-[11px] text-ink/50">
                Auto-discount applied{order.discount_kind === "percent" && order.discount_value ? `: ${Number(order.discount_value)}% off` : order.discount_kind === "free_shipping" ? ": free shipping" : ""}
              </p>
            )}
          </div>
        </div>

        {/* Payment */}
        <div className="mt-5">
          <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Payment</p>
          <div className="mt-2 flex items-center gap-3">
            {order.payment_screenshot_url && (
              <a href={order.payment_screenshot_url} target="_blank" rel="noopener noreferrer">
                <img
                  src={order.payment_screenshot_url}
                  alt="Payment screenshot"
                  className="h-16 w-16 flex-none rounded-xl border border-ink/10 object-cover"
                />
              </a>
            )}
            <div className="min-w-0">
              <p className="text-sm font-semibold text-ink">{paymentMethodLabel(order) || "—"}</p>
              {!order.payment_screenshot_url && (
                <p className="text-xs text-ink/50">No screenshot attached.</p>
              )}
            </div>
            {order.status === "pending_manual_verification" && (
              <div className="flex flex-none gap-2">
                <button
                  onClick={() => onConfirm(order)}
                  disabled={isSubmitting}
                  className="rounded-full bg-mint px-3 py-1.5 text-xs font-bold text-ink disabled:opacity-40"
                >
                  Confirm
                </button>
                <button
                  onClick={() => onReject(order)}
                  disabled={isSubmitting}
                  className="rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-bold text-hotpink disabled:opacity-40"
                >
                  Reject
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Delivery / shipping */}
        {isPaidLike && (
          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Delivery</p>
            <div className="mt-2 rounded-xl bg-ink/[0.03] px-3 py-2.5 text-sm">
              {digital ? (
                <p className="text-ink/60">Digital item — delivered automatically, no shipment needed.</p>
              ) : order.shipped_at ? (
                <div>
                  <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs font-bold uppercase tracking-wide">
                    <span className="text-ink/60">
                      🚚 Shipped
                      <span className="ml-1 font-normal normal-case text-ink/40">
                        ({safeDate(order.shipped_at)?.toLocaleDateString() || "—"})
                      </span>
                    </span>
                    {deliveryMeta && (
                      <>
                        <span className="text-ink/30">→</span>
                        <span className={deliveryMeta.textClassName} title={order.delivery_status_raw || undefined}>
                          {deliveryMeta.icon} {deliveryMeta.label}
                          {order.delivery_status_updated_at && (
                            <span className="ml-1 font-normal normal-case text-ink/40">
                              ({safeDate(order.delivery_status_updated_at)?.toLocaleDateString() || "—"})
                            </span>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                  <p className="mt-1.5 text-xs text-ink/50">
                    {order.courier_name ? `${order.courier_name} · ` : ""}{order.awb_code || "No AWB on file"}
                  </p>
                  {order.tracking_url && (
                    <a href={order.tracking_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block text-xs font-semibold text-hotpink underline decoration-dotted">
                      Track shipment
                    </a>
                  )}
                  {!showDeliveryTracking && <FreeDeliveryUpsell />}
                </div>
              ) : order.return_status === "returned" ? (
                <p className="text-ink/50">Fully returned — never shipped.</p>
              ) : courierConnected ? (
                <p className="text-ink/50">Use "Ship the Order" in the Shipments tab.</p>
              ) : (
                <div>
                  {order.shipment_status === "awb_assigned" && (
                    <p className="text-xs font-semibold text-ink/60">
                      Courier booked · {order.courier_name || "AWB"} {order.awb_code}
                    </p>
                  )}
                  <button
                    onClick={() => onShip(order)}
                    className="mt-1 text-xs font-semibold text-hotpink underline decoration-dotted"
                  >
                    Mark as shipped
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Returns */}
        {isPaidLike && !hideReturns && (
          <div className="mt-5">
            <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Returns</p>
            <div className="mt-2 rounded-xl bg-ink/[0.03] px-3 py-2.5 text-sm">
              <p className="text-xs text-ink/50">{formatReturnWindowStatus(order, windowDays)}</p>
              {(() => {
                const returnedItems = items.filter((it) => it.return_request_status);
                if (returnedItems.length === 0) {
                  return <p className="text-ink/50">No returns logged on this order yet.</p>;
                }
                return (
                  <div className="space-y-1.5">
                    {returnedItems.map((it, idx) => (
                      <div key={it.id ?? idx} className="flex items-center justify-between gap-3">
                        <span className="text-ink/70">
                          {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                        </span>
                        <ItemHistoryTrail events={buildItemHistory(it, order)} />
                      </div>
                    ))}
                  </div>
                );
              })()}
              {(() => {
                const start = getReturnWindowStart(order);
                const daysSince = start ? (Date.now() - start.getTime()) / 86400000 : null;
                return daysSince !== null && daysSince > windowDays && !order.return_fully_covered ? (
                  <p className="mt-2 rounded-lg bg-peach/60 px-2.5 py-1.5 text-xs font-semibold text-ink">
                    It's been {Math.floor(daysSince)} days since the return window opened, outside your {windowDays}-day window.
                  </p>
                ) : null;
              })()}
              {!order.return_fully_covered && (
                <button
                  onClick={() => onLogReturn(order)}
                  className="mt-2 text-xs font-semibold text-hotpink underline decoration-dotted"
                >
                  Log a return
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const initialReturnQty = (order) => {
  const q = {};
  (order.items || []).filter(isItemReturnable).forEach((i) => { q[i.id] = i.quantity; });
  return q;
};

function OrdersPanel({ merchantReady, currency, returnWindowDays, plan, onNotificationsChanged, courierConnected }) {
  const showDiscount = plan === "growth" || plan === "pro";
  const showDeliveryTracking = plan === "starter" || plan === "growth" || plan === "pro";
  const isFree = plan === "free";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [returnDialog, setReturnDialog] = useState(null);
  const [returnReason, setReturnReason] = useState("");
  const [isSubmittingReturn, setIsSubmittingReturn] = useState(false);
  const [selectedQty, setSelectedQty] = useState({}); // { [item_id]: qty } — present = selected
  const [shipDialog, setShipDialog] = useState(null);
  const [isShipping, setIsShipping] = useState(false);
  const [manualAwb, setManualAwb] = useState("");
  const [manualTrackingUrl, setManualTrackingUrl] = useState("");
  const [manualCourierName, setManualCourierName] = useState("");
  const [detailOrder, setDetailOrder] = useState(null);

  const fetchOrders = () => {
    setLoading(true);
    const q = query ? `&search=${encodeURIComponent(query)}` : "";
    const effectivePage = isFree ? 1 : page;
    const effectiveLimit = isFree ? FREE_PLAN_ROW_CAP : 20;
    cachedGet(`/merchant/orders?page=${effectivePage}&limit=${effectiveLimit}${q}`)
      .then((r) =>
        setData(
          plan === "creator"
            ? { ...r.data, items: (r.data.items || []).map((x) => ({ ...x, return_fully_covered: true })) }
            : r.data
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!merchantReady) return;
    fetchOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, merchantReady, isFree]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const submitReturn = async () => {
    if (!returnDialog || !returnReason.trim() || isSubmittingReturn) return;
    setIsSubmittingReturn(true);
    try {
      const allItems = returnDialog.items || [];
      const returnableItems = allItems.filter(isItemReturnable);
      const selectedEntries = Object.entries(selectedQty).map(([id, qty]) => ({ id: Number(id), qty }));
      const isFreshFullOrder =
        returnableItems.length === allItems.length &&
        selectedEntries.length === allItems.length &&
        selectedEntries.every(({ id, qty }) => {
          const item = allItems.find((i) => i.id === id);
          return item && qty === item.quantity;
        });
      await api.post(`/merchant/returns/create`, {
        order_id: returnDialog.id,
        reason: returnReason.trim(),
        order_items: isFreshFullOrder ? [] : selectedEntries,
      });
      toast.success("Return logged.");
      invalidateCache("/merchant/orders");
      invalidateCache("/merchant/returns");
      // Merchant-logged returns are always auto-approved -- update this
      // order's items + its cumulative return_status right here so the
      // badge and "Mark as shipped" reflect it instantly, no refresh needed.
      const applyReturnToOrder = (it) => {
        if (it.id !== returnDialog.id) return it;
        const newItems = (it.items || []).map((x) => {
          const entry = selectedEntries.find((e) => e.id === x.id);
          if (!entry) return x;
          return { ...x, return_request_status: "approved", returned_quantity: entry.qty };
        });
        const fullyReturned = newItems.every((x) => !x.return_request_status || (x.returned_quantity ?? x.quantity) >= x.quantity);
        const anyApproved = newItems.some((x) => x.return_request_status === "approved");
        return {
          ...it,
          items: newItems,
          return_status: fullyReturned ? "returned" : anyApproved ? "partially_returned" : it.return_status,
          return_fully_covered: newItems.every((x) => !!x.return_request_status),
        };
      };
      setData((d) => ({ ...d, items: d.items.map(applyReturnToOrder) }));
      setDetailOrder((cur) => (cur && cur.id === returnDialog.id ? applyReturnToOrder(cur) : cur));
      setReturnDialog(null);
      setReturnReason("");
    } catch (e) {
      const code = e?.response?.data?.error;
      toast.error(
        code === "return_already_exists"
          ? "A return has already been logged for one or more of these items."
          : "Couldn't log the return — try again."
      );
    } finally {
      setIsSubmittingReturn(false);
    }
  };

  const handleAction = async () => {
    if (!confirmDialog || isSubmitting) return;
    const { order, action } = confirmDialog;
    setIsSubmitting(true);
    try {
      await api.post(`/merchant/orders/${action}`, { order_id: order.id });
      toast.success(action === "confirm" ? "Order confirmed" : "Order rejected");
      setConfirmDialog(null);
      setDetailOrder(null);
      invalidateCache("/merchant/orders");
      fetchOrders();
    } catch (e) {
      toast.error("Something went wrong — try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Digital-only orders never get physically shipped — skip the shipping
  // UI, the return window runs from the order date instead (see the bot's
  // return-eligibility query).
  const isDigitalOrder = (o) => Array.isArray(o.items) && o.items.length > 0 && o.items.every((i) => i.is_digital);

    const markShipped = async () => {
    if (!shipDialog || isShipping) return;
    setIsShipping(true);
    try {
      await api.post("/merchant/orders/ship", {
        order_id: shipDialog.id,
        // Optional — only relevant for merchants who don't have Shiprocket
        // auto-ship connected and are typing courier details in by hand.
        awb_code: manualAwb.trim(),
        courier_name: manualCourierName.trim(),
        tracking_url: manualTrackingUrl.trim(),
      });
      toast.success("Marked as shipped.");
      invalidateCache("/merchant/orders");
      invalidateCache("/merchant/shipments");
      // Marking shipped hard-deletes this order's "order_unshipped" row
      // server-side immediately. Invalidate the notifications cache and
      // tell the parent Dashboard so the notification card (if visible)
      // clears right away instead of waiting for a page reload.
      invalidateCache("/merchant/notifications");
      onNotificationsChanged?.();
      const applyShippedToOrder = (x) => (x.id === shipDialog.id ? { ...x, shipped_at: new Date().toISOString() } : x);
      setData((d) => ({ ...d, items: d.items.map(applyShippedToOrder) }));
      setDetailOrder((cur) => (cur && cur.id === shipDialog.id ? applyShippedToOrder(cur) : cur));
      setShipDialog(null);
      setManualAwb("");
      setManualCourierName("");
      setManualTrackingUrl("");
    } catch (e) {
      const code = e?.response?.data?.error;
      const message =
        code === "order_not_found_or_already_shipped"
          ? "This order is already marked as shipped — refreshing…"
          : code === "shipping_locked"
          ? "Upgrade to Starter to use this."
          : "Couldn't mark this as shipped — try again.";
      toast.error(message);
      if (code === "order_not_found_or_already_shipped") {
        invalidateCache("/merchant/orders");
        fetchOrders();
        setShipDialog(null);
      }
    } finally {
      setIsShipping(false);
    }
  };

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="orders-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Orders</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">Placed, paid, pending</h2>
        </div>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={applySearch}
          exportSearch={query}
          exportPath="/merchant/orders/export"
          exportLabel="orders"
          plan={plan}
        />
      </div>

      {/* DESKTOP TABLE — unchanged, just now hidden below md */}
      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[960px] table-fixed text-sm">
          {showDiscount ? (
            <colgroup>
              <col className="w-[9%]" />
              <col className="w-[14%]" />
              <col className="w-[23%]" />
              <col className="w-[7%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[13%]" />
              <col className="w-[10%]" />
            </colgroup>
          ) : (
            <colgroup>
              <col className="w-[10%]" />
              <col className="w-[16%]" />
              <col className="w-[29%]" />
              <col className="w-[7%]" />
              <col className="w-[14%]" />
              <col className="w-[13%]" />
              <col className="w-[11%]" />
            </colgroup>
          )}
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Order #</th>
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Items</th>
              <th className="pb-3 pr-4">Amount</th>
              {showDiscount && <th className="pb-3 pr-4">Discount</th>}
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">
                Screenshot
                <span className="block text-[9px] font-normal normal-case tracking-normal text-ink/35">Auto-deleted after 90 days</span>
              </th>
              <th className="pb-3">Created</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={showDiscount ? 8 : 7} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={showDiscount ? 8 : 7} className="py-6 text-center text-ink/50">No orders yet.</td></tr>
            ) : (
              data.items.map((o) => (
                <tr key={o.id} className="border-b border-ink/5" data-testid={`order-row-${o.id}`}>
                  <td className="py-3 pr-4 align-top font-mono text-xs">
                    <button
                      type="button"
                      onClick={() => setDetailOrder(o)}
                      data-testid={`order-number-${o.id}`}
                      className="text-left underline decoration-dotted hover:text-hotpink"
                      title="View full order details"
                    >
                      {o.order_number || String(o.id ?? "").slice(0, 8)}
                    </button>
                    <div className="mt-1"><OrderSourceBadge source={o.order_source} /></div>
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <div className="flex items-center gap-2">
                      <ProfilePic src={o.profile_pic} className="h-7 w-7 flex-none" iconSize={12} />
                      <div className="min-w-0">
                        {o.customer_ig_handle ? (
                          <a href={igProfileUrl(o.customer_ig_handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Open @${o.customer_ig_handle} on Instagram`}
                            className="block truncate font-semibold text-hotpink hover:underline"
                          >
                            {o.display_name || o.customer_name || "—"}
                          </a>
                        ) : (
                          <p className="truncate font-semibold">{o.display_name || o.customer_name || "—"}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/70">
                    {o.items?.length ? (
                      <ul className="space-y-1 text-xs leading-relaxed">
                        {o.items.map((i, idx) => (
                          <li key={idx} className="break-words">
                            {i.product_name}{i.size ? ` · ${i.size}` : ""} × {i.quantity}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <p className="text-xs text-ink/70">—</p>
                    )}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <OrderAmountBreakdown o={o} currency={currency} />
                  </td>
                  {showDiscount && (
                    <td className="py-3 pr-4 align-top">
                      {o.discount_code ? (
                        <span className="inline-flex items-center rounded-full bg-lilac px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                          {o.discount_code}
                        </span>
                      ) : o.discount_kind ? (
                        <span className="inline-flex items-center rounded-full bg-mint/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                          Auto{o.discount_kind === "percent" && o.discount_value ? `: ${Number(o.discount_value)}% off` : o.discount_kind === "free_shipping" ? ": free shipping" : ""}
                        </span>
                      ) : (
                        <span className="text-xs text-ink/30">—</span>
                      )}
                    </td>
                  )}
                  <td className="py-3 pr-4 align-top">
                    <StateBadge state={o.status} />
                    {(o.status === "paid" || o.status === "paid_manual_review") && (
                      isDigitalOrder(o) ? (
                        <span className="mt-1.5 block text-[10px] font-semibold text-ink/40">Digital · delivered automatically</span>
                      ) : o.shipped_at ? (
                        showDeliveryTracking && o.delivery_status && DELIVERY_STATUS_META[o.delivery_status] ? (
                          <div className={`mt-1.5 text-[10px] font-semibold ${DELIVERY_STATUS_META[o.delivery_status].textClassName}`} title={o.delivery_status_raw || undefined}>
                            <span className="block">
                              {DELIVERY_STATUS_META[o.delivery_status].icon} {DELIVERY_STATUS_META[o.delivery_status].label}
                              {o.courier_name ? ` · ${o.courier_name}` : ""}
                              {o.awb_code ? ` · ${o.awb_code}` : ""}
                            </span>
                            {o.tracking_url && (
                              <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">
                                Track shipment
                              </a>
                            )}
                          </div>
                        ) : (
                          <div className="mt-1.5 text-[10px] font-semibold text-emerald-600">
                            <span className="block">✓ Shipped{o.courier_name ? ` · ${o.courier_name}` : ""}{o.awb_code ? ` · ${o.awb_code}` : ""}</span>
                            {o.tracking_url && (
                              <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">
                                Track shipment
                              </a>
                            )}
                            {isFree && <FreeDeliveryUpsell />}
                          </div>
                        )
                      ) : o.return_status === "returned" ? (
                        <p className="mt-1.5 text-[10px] font-semibold text-ink/40">Fully returned · not shipped</p>
                      ) : (
                        <>
                          {courierConnected && (
                            <p className="mt-1.5 text-[10px] font-semibold text-ink/40">
                              {o.shipment_fail_reason || 'Use "Ship the Order" in the Shipments tab'}
                            </p>
                          )}
                          {!courierConnected && o.shipment_status === "awb_assigned" && (
                            <p className="mt-1.5 text-[10px] font-semibold text-ink/60">
                              Courier booked · {o.courier_name || "AWB"} {o.awb_code}
                            </p>
                          )}
                          <button
                            onClick={() => { setShipDialog(o); setManualAwb(o.awb_code || ""); setManualCourierName(o.courier_name || ""); setManualTrackingUrl(o.tracking_url || ""); }}
                            className="mt-1.5 block text-[10px] font-semibold text-hotpink underline decoration-dotted"
                          >
                            Mark as shipped
                          </button>
                        </>
                      )
                    )}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    {o.payment_screenshot_url ? (
                      <a href={o.payment_screenshot_url} target="_blank" rel="noopener noreferrer">
                        <img src={o.payment_screenshot_url} alt="Payment screenshot" className="h-10 w-10 rounded-lg object-cover border border-ink/10" />
                      </a>
                    ) : (
                      <span className="text-xs text-ink/30">—</span>
                    )}
                    {o.status === "pending_manual_verification" && (
                      <div className="mt-1.5 flex gap-1.5">
                        <button
                          onClick={() => setConfirmDialog({ order: o, action: "confirm" })}
                          disabled={isSubmitting}
                          className="rounded-full bg-mint px-2.5 py-1 text-[10px] font-bold text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Confirm
                        </button>
                        <button
                          onClick={() => setConfirmDialog({ order: o, action: "reject" })}
                          disabled={isSubmitting}
                          className="rounded-full bg-hotpink/10 px-2.5 py-1 text-[10px] font-bold text-hotpink disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          Reject
                        </button>
                      </div>
                    )}
                  </td>
                    <td className="py-3 align-top text-ink/60">
                    <DateTimeCell value={o.created_at} />
                    {(o.status === "paid" || o.status === "paid_manual_review") && (
                      <>
                        {o.return_status && (
                          <span className="mt-1.5 block">
                            <StateBadge state={o.return_status} />
                          </span>
                        )}
                        {o.exchange_status && (
                          <span className="mt-1.5 block">
                            <StateBadge state={o.exchange_status} />
                          </span>
                        )}
                        {!o.return_fully_covered && (
                          <button
                            onClick={() => { setReturnDialog(o); setReturnReason(""); setSelectedQty(initialReturnQty(o)); }}
                            className="mt-1 block text-[10px] font-semibold text-hotpink underline decoration-dotted"
                          >
                            Log a return
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE CARDS */}
      <div className="mt-6 space-y-4 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">No orders yet.</p>
        ) : (
          data.items.map((o) => (
            <div key={o.id} className="rounded-2xl border border-ink/10 p-4" data-testid={`order-card-${o.id}`}>
              {/* Order # + Status */}
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Order #</p>
                  <button
                    type="button"
                    onClick={() => setDetailOrder(o)}
                    data-testid={`order-number-mobile-${o.id}`}
                    className="mt-0.5 block font-mono text-xs font-semibold text-hotpink underline decoration-dotted"
                  >
                    {o.order_number || String(o.id ?? "").slice(0, 8)}
                  </button>
                  <div className="mt-1"><OrderSourceBadge source={o.order_source} /></div>
                </div>
                <div className="flex-none text-right">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Status</p>
                  <div className="mt-1"><StateBadge state={o.status} /></div>
                  {(o.status === "paid" || o.status === "paid_manual_review") && (
                    isDigitalOrder(o) ? (
                      <span className="mt-1.5 block text-right text-[10px] font-semibold text-ink/40">Digital · delivered automatically</span>
                    ) : o.shipped_at ? (
                      showDeliveryTracking && o.delivery_status && DELIVERY_STATUS_META[o.delivery_status] ? (
                        <div className={`mt-1.5 text-right text-[10px] font-semibold ${DELIVERY_STATUS_META[o.delivery_status].textClassName}`} title={o.delivery_status_raw || undefined}>
                          <span className="block">
                            {DELIVERY_STATUS_META[o.delivery_status].icon} {DELIVERY_STATUS_META[o.delivery_status].label}
                            {o.courier_name ? ` · ${o.courier_name}` : ""}
                            {o.awb_code ? ` · ${o.awb_code}` : ""}
                          </span>
                          {o.tracking_url && (
                            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">
                              Track shipment
                            </a>
                          )}
                        </div>
                      ) : (
                        <div className="mt-1.5 text-[10px] font-semibold text-emerald-600">
                          <span className="block">✓ Shipped{o.courier_name ? ` · ${o.courier_name}` : ""}{o.awb_code ? ` · ${o.awb_code}` : ""}</span>
                          {o.tracking_url && (
                            <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline decoration-dotted">
                              Track shipment
                            </a>
                          )}
                          {isFree && <FreeDeliveryUpsell className="justify-end text-right" />}
                        </div>
                      )
                    ) : o.return_status === "returned" ? (
                      <p className="mt-1.5 text-right text-[10px] font-semibold text-ink/40">Fully returned · not shipped</p>
                    ) : courierConnected ? (
                      <p className="mt-1.5 text-right text-[10px] font-semibold text-ink/40">Use "Ship the Order" in the Shipments tab</p>
                    ) : (
                      <>
                        {o.shipment_status === "awb_assigned" && (
                          <p className="mt-1.5 text-[10px] font-semibold text-ink/60">
                            Courier booked · {o.courier_name || "AWB"} {o.awb_code}
                          </p>
                        )}
                        <button
                          onClick={() => { setShipDialog(o); setManualAwb(o.awb_code || ""); setManualCourierName(o.courier_name || ""); setManualTrackingUrl(o.tracking_url || ""); }}
                          className="mt-1.5 block text-[10px] font-semibold text-hotpink underline decoration-dotted"
                        >
                          Mark as shipped
                        </button>
                      </>
                    )
                  )}
                </div>
              </div>

              {/* Customer */}
              <div className="mt-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Customer</p>
                <div className="mt-1 flex items-center gap-2">
                  <ProfilePic src={o.profile_pic} className="h-7 w-7 flex-none" iconSize={12} />
                  <div className="min-w-0">
                    {o.customer_ig_handle ? (
                      <a
                        href={igProfileUrl(o.customer_ig_handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        title={`Open @${o.customer_ig_handle} on Instagram`}
                        className="truncate font-semibold text-hotpink hover:underline block"
                      >
                        {o.display_name || o.customer_name || "—"}
                      </a>
                    ) : (
                      <p className="truncate font-semibold">{o.display_name || o.customer_name || "—"}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="mt-3">
                <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Items</p>
                {o.items?.length ? (
                  <ul className="mt-1 space-y-1 text-xs leading-relaxed text-ink/70">
                    {o.items.map((i, idx) => (
                      <li key={idx} className="break-words">
                        {i.product_name}{i.size ? ` · ${i.size}` : ""} × {i.quantity}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-1 text-xs text-ink/70">—</p>
                )}
              </div>

              {/* Amount + Discount */}
              <div className="mt-3 flex flex-wrap items-start justify-between gap-4">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Amount</p>
                  <div className="mt-1"><OrderAmountBreakdown o={o} currency={currency} /></div>
                </div>
                {showDiscount && (o.discount_code || o.discount_kind) && (
                  <div className="text-right">
                    <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Discount</p>
                    <div className="mt-1">
                      {o.discount_code ? (
                        <span className="inline-flex items-center rounded-full bg-lilac px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                          {o.discount_code}
                        </span>
                      ) : (
                        <span className="inline-flex items-center rounded-full bg-mint/60 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-ink">
                          Auto{o.discount_kind === "percent" && o.discount_value ? `: ${Number(o.discount_value)}% off` : o.discount_kind === "free_shipping" ? ": free shipping" : ""}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Payment screenshot */}
              {o.payment_screenshot_url && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Payment screenshot</p>
                  <a href={o.payment_screenshot_url} target="_blank" rel="noopener noreferrer" className="mt-1 inline-block">
                    <img src={o.payment_screenshot_url} alt="Payment screenshot" className="h-14 w-14 rounded-lg object-cover border border-ink/10" />
                  </a>
                </div>
              )}

              {/* Verify payment — only for manual verification */}
              {o.status === "pending_manual_verification" && (
                <div className="mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Verify payment</p>
                  <div className="mt-1 flex gap-2">
                    <button
                      onClick={() => setConfirmDialog({ order: o, action: "confirm" })}
                      disabled={isSubmitting}
                      className="flex-1 rounded-full bg-mint px-3 py-1.5 text-xs font-bold text-ink disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Confirm
                    </button>
                    <button
                      onClick={() => setConfirmDialog({ order: o, action: "reject" })}
                      disabled={isSubmitting}
                      className="flex-1 rounded-full bg-hotpink/10 px-3 py-1.5 text-xs font-bold text-hotpink disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              )}

              {/* Created + Return */}
              <div className="mt-3 flex items-end justify-between border-t border-ink/5 pt-3">
                <div>
                  <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Created</p>
                  <div className="mt-0.5 text-xs text-ink/60"><DateTimeCell value={o.created_at} /></div>
                </div>
                {(o.status === "paid" || o.status === "paid_manual_review") && (
                  <div className="text-right">
                    {o.return_status && (
                      <>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-ink/40">Return</p>
                        <div className="mt-1"><StateBadge state={o.return_status} /></div>
                      </>
                    )}
                    {o.exchange_status && (
                      <>
                        <p className="mt-1 text-[10px] font-bold uppercase tracking-wide text-ink/40">Exchange</p>
                        <div className="mt-1"><StateBadge state={o.exchange_status} /></div>
                      </>
                    )}
                    {!o.return_fully_covered && (
                      <button
                        onClick={() => { setReturnDialog(o); setReturnReason(""); setSelectedQty(initialReturnQty(o)); }}
                        className="mt-1 text-xs font-semibold text-hotpink underline decoration-dotted"
                      >
                        Log a return
                      </button>
                    )}
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setDetailOrder(o)}
                className="mt-3 w-full rounded-full border border-ink/15 bg-white px-3 py-1.5 text-xs font-semibold"
              >
                View full order details
              </button>
            </div>
          ))
        )}
      </div>

      {isFree ? (
        <FreePlanRowCap shown={data.items.length} total={data.total} label="orders" />
      ) : (
        <Pager page={page} total={data.total} limit={data.limit} onPage={setPage} testPrefix="orders" />
      )}

      <OrderDetailSheet
        order={detailOrder}
        open={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        currency={currency}
        returnWindowDays={returnWindowDays}
        showDiscount={showDiscount}
        showDeliveryTracking={showDeliveryTracking}
        isDigitalOrder={isDigitalOrder}
        courierConnected={courierConnected}
        hideReturns={plan === "creator"}
        onConfirm={(o) => setConfirmDialog({ order: o, action: "confirm" })}
        onReject={(o) => setConfirmDialog({ order: o, action: "reject" })}
        onShip={(o) => { setShipDialog(o); setManualAwb(o.awb_code || ""); setManualCourierName(o.courier_name || ""); setManualTrackingUrl(o.tracking_url || ""); }}
        onLogReturn={(o) => { setReturnDialog(o); setReturnReason(""); setSelectedQty(initialReturnQty(o)); }}
        isSubmitting={isSubmitting}
      />

      {confirmDialog && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
            <p className="font-display text-xl font-black tracking-tight">
              {confirmDialog.action === "confirm" ? "Confirm this order?" : "Reject this order?"}
            </p>
            <p className="mt-2 text-sm text-ink/60">
              Order {confirmDialog.order.order_number} for {confirmDialog.order.customer_name || "this customer"}.
              {confirmDialog.action === "confirm"
                ? " They'll get a DM (only if within Instagram's 24h window) and an email confirmation."
                : " They'll get a DM (only if within Instagram's 24h window) and a rejection email. You are suggested to contact the buyer directly to sort any dispute."}
            </p>
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setConfirmDialog(null)}
                disabled={isSubmitting}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleAction}
                disabled={isSubmitting}
                className={`flex-1 rounded-full py-2.5 text-sm font-semibold text-white disabled:opacity-60 disabled:cursor-not-allowed ${confirmDialog.action === "confirm" ? "bg-sunset-gradient" : "bg-hotpink"}`}
              >
                {isSubmitting ? "Please wait…" : `Yes, ${confirmDialog.action}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {shipDialog && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6 text-center">
            <p className="font-display text-xl font-black tracking-tight">Mark this order as shipped?</p>
            <p className="mt-2 text-sm text-ink/60">
              {shipDialog.display_name || shipDialog.customer_name || "The customer"} will get an email letting them know their order is on the way.
            </p>

            <div className="mt-4 space-y-3 text-left">
              <p className="text-xs text-ink/50">
                Optional — add courier details if you have them and they'll be included in that email.
              </p>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/60">
                  Courier partner
                </span>
                <input
                  type="text"
                  value={manualCourierName}
                  onChange={(e) => setManualCourierName(e.target.value)}
                  placeholder="e.g. Delhivery, Bluedart, DTDC"
                  className="w-full rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/60">
                  AWB / Tracking number
                </span>
                <input
                  type="text"
                  value={manualAwb}
                  onChange={(e) => setManualAwb(e.target.value)}
                  placeholder="e.g. SR1234567890"
                  className="w-full rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
                />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-ink/60">
                  Tracking link
                </span>
                <input
                  type="text"
                  value={manualTrackingUrl}
                  onChange={(e) => setManualTrackingUrl(e.target.value)}
                  placeholder="https://…"
                  className="w-full rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
                />
              </label>
            </div>

            <div className="mt-5 flex gap-3">
              <button
                onClick={() => { setShipDialog(null); setManualAwb(""); setManualCourierName(""); setManualTrackingUrl(""); }}
                disabled={isShipping}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={markShipped}
                disabled={isShipping}
                className="flex-1 rounded-full bg-sunset-gradient py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isShipping ? "Please wait…" : "Yes, mark shipped"}
              </button>
            </div>
          </div>
        </div>
      )}

      {returnDialog && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm md:max-w-lg max-h-[85vh] overflow-y-auto rounded-3xl bg-white p-6">
            <div className="flex items-start gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                <Undo2 size={20} />
              </span>
              <div>
                <p className="font-display text-xl font-black tracking-tight">Log a return</p>
                <p className="mt-1 text-sm text-ink/60">
                  Order {returnDialog.order_number} for {returnDialog.customer_name || "this customer"}
                </p>
              </div>
            </div>

            <p className="mt-4 text-sm text-ink/60">
              This creates an already-approved return — use it when you know the item needs to go back (damaged, wrong item sent, etc).
            </p>

            <div className="mt-3 flex items-start gap-2 rounded-2xl bg-lilac/30 px-3 py-2.5 text-xs text-ink/70">
              <Info size={14} className="mt-0.5 flex-none text-ink/40" />
              <span>The reason you write below will be included in the confirmation email the customer receives.</span>
            </div>

            {(() => {
              const windowDays = returnWindowDays ?? 7;
              const start = getReturnWindowStart(returnDialog);
              const notStartedYet = !start || start.getTime() > Date.now();
              const daysSince = notStartedYet ? null : (Date.now() - start.getTime()) / 86400000;
              const isOutside = daysSince !== null && daysSince > windowDays;
              return (
                <div className="mt-3 space-y-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                      isOutside ? "bg-peach text-ink" : notStartedYet ? "bg-ink/5 text-ink/50" : "bg-mint text-ink"
                    }`}
                  >
                    {isOutside ? "⏳" : notStartedYet ? "•" : "✓"} {formatReturnWindowStatus(returnDialog, windowDays)}
                  </span>
                  {isOutside && (
                    <p className="rounded-2xl bg-peach/60 px-3 py-2 text-xs font-semibold text-ink">
                      Heads up — it's been {Math.floor(daysSince)} days since the return window opened, outside your {windowDays}-day window. You can still log it; this is just a flag.
                    </p>
                  )}
                </div>
              );
            })()}

            {(() => {
              const allItems = returnDialog.items || [];
              const needsItemPicker = allItems.length > 1 || allItems.some((it) => !isItemReturnable(it) || it.quantity > 1);
              if (!needsItemPicker) return null;
              const returnableItems = allItems.filter(isItemReturnable);
              // "Decided" = already returned/rejected, or an exchange on it was
              // approved (swapped) — nothing left to do, just show the trail.
              const decidedItems = allItems.filter((it) => it.return_request_status || it.exchange_request_status === "approved");
              // "Frozen" = an exchange request is sitting open on it. Not hidden,
              // not selectable — its fate gets decided in the Returns tab first.
              const frozenItems = allItems.filter((it) => !it.return_request_status && it.exchange_request_status === "requested");
              const deliveryMeta = showDeliveryTracking && returnDialog.delivery_status && DELIVERY_STATUS_META[returnDialog.delivery_status];
              const DeliveryTag = () =>
                deliveryMeta ? (
                  <span className={`flex-none text-[10px] font-semibold uppercase tracking-wide ${deliveryMeta.textClassName}`} title={returnDialog.delivery_status_raw || undefined}>
                    {deliveryMeta.icon} {deliveryMeta.label}
                  </span>
                ) : null;
              return (
                <div className="mt-4 rounded-2xl bg-ink/[0.03] p-3">
                  <p className="text-xs font-bold uppercase tracking-wide text-ink/50">Items on this order</p>
                  <div className="mt-2 space-y-1.5">
                    {decidedItems.map((it) => (
                      <div key={it.id} className="flex items-center justify-between gap-2 text-sm text-ink/40">
                        <span>{it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}</span>
                        <ItemHistoryTrail events={buildItemHistory(it, returnDialog)} />
                      </div>
                    ))}
                    {frozenItems.map((it) => (
                      <div key={it.id} className="flex items-center justify-between gap-2 text-sm text-ink/40" title="An exchange request on this item hasn't been decided yet — approve or reject it in the Returns tab before it can be returned.">
                        <span className="flex items-center gap-1.5">
                          <input type="checkbox" checked={false} disabled className="accent-hotpink opacity-40" />
                          {it.product_name}{it.size ? ` · ${it.size}` : ""} × {it.quantity}
                        </span>
                        <span className="flex-none rounded-full bg-[#4C1D95]/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-[#4C1D95]">
                          ⇄ Exchange pending
                        </span>
                      </div>
                    ))}
                    {returnableItems.map((it) => {
                      const checked = it.id in selectedQty;
                      const qty = selectedQty[it.id] ?? it.quantity;
                      return (
                        <div key={it.id} className="flex items-center justify-between gap-2 text-sm">
                          <label className="flex flex-1 items-center gap-2">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={(e) =>
                                setSelectedQty((q) => {
                                  const next = { ...q };
                                  if (e.target.checked) next[it.id] = it.quantity;
                                  else delete next[it.id];
                                  return next;
                                })
                              }
                              className="accent-hotpink"
                            />
                            <span>{it.product_name}{it.size ? ` · ${it.size}` : ""} · ordered {it.quantity}</span>
                          </label>
                          {checked && it.quantity > 1 && (
                            <input
                              type="number"
                              min={1}
                              max={it.quantity}
                              value={qty}
                              onChange={(e) => {
                                const v = Math.max(1, Math.min(it.quantity, parseInt(e.target.value, 10) || 1));
                                setSelectedQty((q) => ({ ...q, [it.id]: v }));
                              }}
                              className="w-14 flex-none rounded-lg border border-ink/15 px-2 py-1 text-center text-sm"
                            />
                          )}
                          <DeliveryTag />
                        </div>
                      );
                    })}
                  </div>
                  {returnableItems.length > 1 && (
                    <p className="mt-1 text-[11px] text-ink/40">Leave all checked for a full return of what's left.</p>
                  )}
                </div>
              );
            })()}

            {Number(returnDialog.shipping_fee_charged) > 0 && (() => {
              const allItems = returnDialog.items || [];
              const returnableItems = allItems.filter(isItemReturnable);
              const isFreshFullOrder =
                returnableItems.length === allItems.length &&
                Object.keys(selectedQty).length === allItems.length &&
                returnableItems.every((it) => selectedQty[it.id] === it.quantity);
              return (
                <div className="mt-4 flex items-start gap-2.5 rounded-2xl bg-peach/30 px-3 py-2.5 text-xs text-ink/70">
                  <Wallet size={14} className="mt-0.5 flex-none text-ink/50" />
                  <div>
                    <p className="font-semibold text-ink">
                      Shipping charged on this order: {formatMoney(returnDialog.shipping_fee_charged, currency)}
                    </p>
                    <p className="mt-0.5">
                      {!isFreshFullOrder
                        ? "Partial return — shipping is typically kept, since the rest of the order still shipped."
                        : "Full return — you may want to refund shipping along with the item(s). CheckoutDM doesn't process refunds automatically; you'll do this manually via Razorpay or your bank."}
                    </p>
                  </div>
                </div>
              );
            })()}

            <textarea
              rows={3}
              value={returnReason}
              onChange={(e) => setReturnReason(e.target.value)}
              placeholder="e.g. Item arrived damaged, customer requested exchange for size M"
              className="mt-4 w-full resize-none rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
            />
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => { setReturnDialog(null); setReturnReason(""); }}
                disabled={isSubmittingReturn}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submitReturn}
                disabled={isSubmittingReturn || !returnReason.trim()}
                className="cta-glow flex-1 rounded-full bg-sunset-gradient py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmittingReturn ? "Logging…" : "Log return"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PartialTag() {
  return (
    <span className="rounded-full bg-lilac px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink">
      Partial return
    </span>
  );
}

function ExchangeTag() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-[#4C1D95] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
      ⇄ Exchange
    </span>
  );
}

// True once the order has shipped but delivery hasn't been confirmed yet.
// Approving a return before this point means different things per provider:
//   - Shiprocket: reverse pickup can't be scheduled yet -- Shiprocket
//     requires a delivered order to create the return order.
//   - Shippo: the return label still gets emailed to the customer right
//     away, but they're being asked to send back a package that may not
//     have reached them yet.
// Only meaningful for orders shipped through Shiprocket or Shippo --
// delivery_status is never populated for manually-entered AWBs or
// non-integrated couriers, so without this check the badge would show
// forever for those orders instead of just never applying to them.
function isOrderInTransit(r) {
  return r.courier_tracking_state === "in_transit";
}

function InTransitTag({ isShippo, isManual }) {
  const [open, setOpen] = useState(false);
  return (
    <span className="group relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className="inline-flex cursor-help items-center gap-1 rounded-full bg-peach px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide text-ink"
      >
        🚚 In transit
      </button>
      <span
        className={`absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink/10 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg ${
          open ? "block" : "hidden md:group-hover:block"
        }`}
      >
        {isManual
          ? "This order shipped less than 2 days ago, so it's assumed to still be in transit (no courier is connected). You can still approve the return, but the customer may not have the item yet — you'll arrange the pickup yourself."
          : isShippo
          ? "The package hasn't been delivered yet. If you approve now, the customer gets a prepaid return label by email immediately — but they may not have the item in hand yet. Consider waiting until delivery is confirmed."
          : "If you approve this return now, reverse pickup won't be scheduled yet — the package is still in transit and hasn't reached the customer. After the package is delivered, you can Approve the Return. If you reject it now, customer won't be able to create a new RETURN REQUEST for this item. You will have to manually log the RETURN REQUEST later."}
      </span>
    </span>
  );
}

function isOrderDelivered(r) {
  return r.courier_tracking_state === "delivered";
}

function DeliveredTag({ isShippo, isManual }) {
  const [open, setOpen] = useState(false);
  const meta = DELIVERY_STATUS_META.delivered;
  return (
    <span className="group relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${meta.pillClassName}`}
      >
        {meta.icon} {meta.label}
      </button>
      <span
        className={`absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink/10 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg ${
          open ? "block" : "hidden md:group-hover:block"
        }`}
      >
        {isManual
          ? "It's been over 2 days since you marked this order shipped, so it's assumed delivered. Go ahead and Approve this return — you'll arrange the pickup yourself."
          : isShippo
          ? "The courier has confirmed delivery. Go ahead and Approve this return — the customer will get a prepaid return label by email."
          : "The courier has confirmed delivery. Reverse pickup can be scheduled normally — go ahead and Approve this return."}
      </span>
    </span>
  );
}

function ExchangeDeliveryTag({ delivered }) {
  const [open, setOpen] = useState(false);
  const meta = DELIVERY_STATUS_META.delivered;
  return (
    <span className="group relative inline-block">
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }}
        className={`inline-flex cursor-help items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-bold uppercase tracking-wide ${delivered ? meta.pillClassName : "bg-peach text-ink"}`}
      >
        {delivered ? `${meta.icon} ${meta.label}` : "🚚 Not delivered yet"}
      </button>
      <span
        className={`absolute left-0 top-full z-20 mt-1 w-56 rounded-xl border border-ink/10 bg-white p-2.5 text-left text-[11px] font-normal normal-case leading-snug text-ink shadow-lg ${
          open ? "block" : "hidden md:group-hover:block"
        }`}
      >
        {delivered
          ? "Delivery confirmed — you can approve this exchange; stock is swapped automatically."
          : "An exchange can only be approved after delivery. You can reject it, or wait until delivery is confirmed."}
      </span>
    </span>
  );
}

function ReturnsPanel({ merchantReady, plan, currency, returnWindowDays, onNotificationsChanged }) {
  const isFree = plan === "free";
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [decisionDialog, setDecisionDialog] = useState(null); // { returnItem, decision }
  const [merchantNote, setMerchantNote] = useState("");
  const [isDeciding, setIsDeciding] = useState(false);

  const load = () => {
    setLoading(true);
    const q = query ? `&search=${encodeURIComponent(query)}` : "";
    const effectivePage = isFree ? 1 : page;
    const effectiveLimit = isFree ? FREE_PLAN_ROW_CAP : 20;
    cachedGet(`/merchant/returns?page=${effectivePage}&limit=${effectiveLimit}${q}`)
      .then((r) =>
        setData(
          isFree
            ? { ...r.data, items: (r.data.items || []).map((x) => ({ ...x, courier_tracking_state: null })) }
            : r.data
        )
      )
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!merchantReady) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, query, merchantReady, isFree]);

  const applySearch = (e) => {
    e.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const submitDecision = async () => {
    if (!decisionDialog || isDeciding) return;
    setIsDeciding(true);
    try {
      const isExchange = decisionDialog.returnItem.kind === "exchange";
      await api.post(isExchange ? `/merchant/exchanges/decision` : `/merchant/returns/decision`, {
        ...(isExchange ? { exchange_id: decisionDialog.returnItem.id } : { return_id: decisionDialog.returnItem.id }),
        decision: decisionDialog.decision,
        merchant_note: merchantNote.trim(),
      });
      if (isExchange && decisionDialog.decision === "approved") {
        toast.success("Exchange approved — now ship the new size.", {
          description: "CheckoutDM doesn't create a shipment for exchanges. Book the replacement yourself on your courier or shipping aggregator's website (Shiprocket, Shippo, etc.).",
          duration: 12000,
        });
      } else {
        toast.success(`${isExchange ? "Exchange" : "Return"} ${decisionDialog.decision === "approved" ? "approved" : "rejected"}.`);
      }
      setDecisionDialog(null);
      setMerchantNote("");
      invalidateCache("/merchant/orders");
      invalidateCache("/merchant/returns");
      invalidateCache("/merchant/notifications");
      onNotificationsChanged?.();
      load();
    } catch (e) {
      const code = e?.response?.data?.error;
      toast.error(
        code === "exchange_not_delivered"
          ? isFree
            ? "This order hasn't been delivered yet, so the exchange can't be approved. Upgrade to Starter to see live delivery status."
            : "This order hasn't been delivered yet — you can approve the exchange once delivery is confirmed."
          : code === "exchange_size_out_of_stock"
          ? "That size just went out of stock — ask the customer to pick another size, or reject this request."
          : "Couldn't save that decision — try again."
      );
    } finally {
      setIsDeciding(false);
    }
  };

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="returns-panel">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Return requests</p>
          <h2 className="mt-1 font-display text-2xl font-black tracking-tight">What buyers want back</h2>
        </div>
        <ListToolbar
          search={search}
          onSearchChange={setSearch}
          onSearchSubmit={applySearch}
          exportSearch={query}
          exportPath="/merchant/returns/export"
          exportLabel="returns"
          plan={plan}
        />
      </div>

      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[820px] table-fixed text-sm">
          <colgroup>
            <col className="w-[12%]" />
            <col className="w-[16%]" />
            <col className="w-[28%]" />
            <col className="w-[14%]" />
            <col className="w-[13%]" />
            <col className="w-[17%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Order #</th>
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">Reason</th>
              <th className="pb-3 pr-4">Status</th>
              <th className="pb-3 pr-4">Requested</th>
              <th className="pb-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-ink/50">No return requests yet.</td></tr>
            ) : (
              data.items.map((r) => (
                <tr key={r.row_key} className={`border-b border-ink/5 ${r.kind === "exchange" ? "bg-[#F4F1FF]" : ""}`} data-testid={`return-row-${r.row_key}`}>
                  <td className="py-3 pr-4 align-top font-mono text-xs">{r.order_number || String(r.order_id ?? "").slice(0, 8)}</td>
                  <td className="py-3 pr-4 align-top">
                    <div className="flex items-center gap-2">
                      <ProfilePic src={r.profile_pic} className="h-7 w-7 flex-none" iconSize={12} />
                      <div className="min-w-0">
                        {r.customer_ig_handle ? (
                          <a href={igProfileUrl(r.customer_ig_handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Open @${r.customer_ig_handle} on Instagram`}
                            className="block truncate font-semibold text-hotpink hover:underline"
                          >
                            {r.display_name || r.customer_name || "—"}
                          </a>
                        ) : (
                          <p className="truncate font-semibold">{r.display_name || r.customer_name || "—"}</p>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/70">
                    {r.kind === "exchange" ? (
                      <>
                        <div className="mb-1"><ExchangeTag /></div>
                        <p className="text-xs leading-relaxed break-words">
                          {(r.return_items && r.return_items[0] && r.return_items[0].product_name) || "Item"}
                          {r.from_size ? ` · size ${r.from_size} → ${r.to_size}` : ""}
                          {r.return_items?.[0]?.quantity ? ` × ${r.return_items[0].quantity}` : ""}
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="text-xs leading-relaxed break-words">{r.reason}</p>
                        <ReturnItemsToggle items={r.return_items} currency={currency} />
                      </>
                    )}
                  </td>
                  <td className="py-3 pr-4 align-top">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <StateBadge
                        label={
                          r.kind === "exchange" && r.status === "requested"
                            ? "Requested"
                            : r.kind === "exchange" && r.status === "rejected"
                            ? "Rejected"
                            : undefined
                        }
                        state={
                          r.kind === "exchange"
                            ? r.status === "approved"
                              ? "exchanged"
                              : r.status === "rejected"
                              ? "exchange_rejected"
                              : "exchange_requested"
                            : r.status === "rejected"
                            ? "return_rejected"
                            : r.status
                        }
                      />
                      {r.kind !== "exchange" && r.is_partial && <PartialTag />}
                      {r.kind !== "exchange" && r.status === "requested" && isOrderInTransit(r) && <InTransitTag isShippo={r.courier_tracking_provider === "shippo"} isManual={r.courier_tracking_provider === "manual"} />}
                      {r.kind !== "exchange" && r.status === "requested" && isOrderDelivered(r) && <DeliveredTag isShippo={r.courier_tracking_provider === "shippo"} isManual={r.courier_tracking_provider === "manual"} />}
                      {!isFree && r.kind === "exchange" && r.status === "requested" && <ExchangeDeliveryTag delivered={isOrderDelivered(r)} />}
                    </div>
                  </td>
                  <td className="py-3 pr-4 align-top text-ink/60"><DateTimeCell value={r.created_at} /></td>
                  <td className="py-3 align-top">
                    {r.status === "requested" ? (
                      <div className="flex gap-1.5">
                        <button
                          onClick={() => { setDecisionDialog({ returnItem: r, decision: "approved" }); setMerchantNote(""); }}
                          className="rounded-full bg-mint px-2.5 py-1 text-[10px] font-bold text-ink"
                        >
                          Approve
                        </button>
                        <button
                          onClick={() => { setDecisionDialog({ returnItem: r, decision: "rejected" }); setMerchantNote(""); }}
                          className="rounded-full bg-hotpink/10 px-2.5 py-1 text-[10px] font-bold text-hotpink"
                        >
                          Reject
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-ink/40">Decided</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Phone layout: one card per return/exchange, no sideways scrolling */}
      <div className="mt-6 space-y-3 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">No return requests yet.</p>
        ) : (
          data.items.map((r) => (
            <div
              key={r.row_key}
              className={`rounded-2xl border border-ink/10 p-4 ${r.kind === "exchange" ? "bg-[#F4F1FF]" : ""}`}
              data-testid={`return-card-${r.row_key}`}
            >
              <div className="flex items-center gap-3">
                <ProfilePic src={r.profile_pic} className="h-10 w-10 flex-none" iconSize={16} />
                <div className="min-w-0 flex-1">
                  {r.customer_ig_handle ? (
                    <a
                      href={igProfileUrl(r.customer_ig_handle)}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="block truncate font-semibold text-hotpink hover:underline"
                    >
                      {r.display_name || r.customer_name || "—"}
                    </a>
                  ) : (
                    <p className="truncate font-semibold">{r.display_name || r.customer_name || "—"}</p>
                  )}
                  <p className="font-mono text-[11px] text-ink/50">
                    Order {r.order_number || String(r.order_id ?? "").slice(0, 8)}
                  </p>
                </div>
                <div className="text-right text-[11px] text-ink/50">
                  <DateTimeCell value={r.created_at} />
                </div>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-1.5">
                <StateBadge
                  label={
                    r.kind === "exchange" && r.status === "requested"
                      ? "Requested"
                      : r.kind === "exchange" && r.status === "rejected"
                      ? "Rejected"
                      : undefined
                  }
                  state={
                    r.kind === "exchange"
                      ? r.status === "approved"
                        ? "exchanged"
                        : r.status === "rejected"
                        ? "exchange_rejected"
                        : "exchange_requested"
                      : r.status === "rejected"
                      ? "return_rejected"
                      : r.status
                  }
                />
                {r.kind !== "exchange" && r.is_partial && <PartialTag />}
                {r.kind !== "exchange" && r.status === "requested" && isOrderInTransit(r) && <InTransitTag isShippo={r.courier_tracking_provider === "shippo"} isManual={r.courier_tracking_provider === "manual"} />}
                {r.kind !== "exchange" && r.status === "requested" && isOrderDelivered(r) && <DeliveredTag isShippo={r.courier_tracking_provider === "shippo"} isManual={r.courier_tracking_provider === "manual"} />}
                {!isFree && r.kind === "exchange" && r.status === "requested" && <ExchangeDeliveryTag delivered={isOrderDelivered(r)} />}
              </div>

              <div className="mt-3 text-xs text-ink/70">
                {r.kind === "exchange" ? (
                  <>
                    <div className="mb-1"><ExchangeTag /></div>
                    <p className="leading-relaxed break-words">
                      {(r.return_items && r.return_items[0] && r.return_items[0].product_name) || "Item"}
                      {r.from_size ? ` · size ${r.from_size} → ${r.to_size}` : ""}
                      {r.return_items?.[0]?.quantity ? ` × ${r.return_items[0].quantity}` : ""}
                    </p>
                  </>
                ) : (
                  <>
                    <p className="leading-relaxed break-words">{r.reason}</p>
                    <ReturnItemsToggle items={r.return_items} currency={currency} />
                  </>
                )}
              </div>

              {r.status === "requested" ? (
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => { setDecisionDialog({ returnItem: r, decision: "approved" }); setMerchantNote(""); }}
                    className="flex-1 rounded-full bg-mint px-3 py-2 text-xs font-bold text-ink"
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => { setDecisionDialog({ returnItem: r, decision: "rejected" }); setMerchantNote(""); }}
                    className="flex-1 rounded-full bg-hotpink/10 px-3 py-2 text-xs font-bold text-hotpink"
                  >
                    Reject
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-xs text-ink/40">Decided</p>
              )}
            </div>
          ))
        )}
      </div>

      {isFree ? (
        <FreePlanRowCap shown={data.items.length} total={data.total} label="returns" />
      ) : (
        <Pager page={page} total={data.total} limit={data.limit} onPage={setPage} testPrefix="returns" />
      )}

      {decisionDialog && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
          <div className="w-full max-w-sm rounded-3xl bg-white p-6">
            <p className="font-display text-xl font-black tracking-tight">
              {decisionDialog.decision === "approved"
                ? `Approve this ${decisionDialog.returnItem.kind === "exchange" ? "exchange" : "return"}?`
                : `Reject this ${decisionDialog.returnItem.kind === "exchange" ? "exchange" : "return"}?`}
            </p>
            <p className="mt-2 text-sm text-ink/60">
              Order {decisionDialog.returnItem.order_number} for {decisionDialog.returnItem.customer_name || "this customer"}.
              {decisionDialog.returnItem.kind !== "exchange" && decisionDialog.returnItem.is_partial ? " This covers only some of the order's items." : ""}
              {decisionDialog.decision === "approved"
                ? (decisionDialog.returnItem.kind === "exchange"
                    ? " Stock gets swapped automatically. They'll get a DM (only if within Instagram's 24h window)."
                    : " Stock gets restocked automatically. They'll get a DM (only if within Instagram's 24h window).")
                : " They'll get a DM (only if within Instagram's 24h window) explaining why, along with your return policy."}
            </p>
            {(() => {
              const windowDays = returnWindowDays ?? 7;
              const r = decisionDialog.returnItem;
              const shimOrder = {
                created_at: r.order_created_at,
                is_digital_order: r.is_digital_order,
                shipped_at: r.shipped_at,
                shiprocket_shipment_id: r.shiprocket_shipment_id,
                shippo_tracking_code: r.shippo_tracking_code,
                delivery_status: r.delivery_status,
                delivery_status_updated_at: r.delivery_status_updated_at,
              };
              const start = getReturnWindowStart(shimOrder);
              const notStartedYet = !start || start.getTime() > Date.now();
              const daysSince = notStartedYet ? null : (Date.now() - start.getTime()) / 86400000;
              const isOutside = daysSince !== null && daysSince > windowDays;
              return (
                <div className="mt-3 space-y-2">
                  <span
                    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
                      isOutside ? "bg-peach text-ink" : notStartedYet ? "bg-ink/5 text-ink/50" : "bg-mint text-ink"
                    }`}
                  >
                    {isOutside ? "⏳" : notStartedYet ? "•" : "✓"} {formatReturnWindowStatus(shimOrder, windowDays)}
                  </span>
                </div>
              );
            })()}
            {isFree && !decisionDialog.returnItem.is_digital_order && (
              <div className="mt-2 flex items-start gap-2.5 rounded-xl bg-lilac/30 px-3 py-2.5 text-xs text-ink/70">
                <Lock size={14} className="mt-0.5 flex-none text-ink/40" />
                <div>
                  <p className="font-semibold text-ink">Delivery status isn't available on the Free plan</p>
                  <p className="mt-0.5">
                    You won't see whether this order was delivered before you decide.{" "}
                    <a href="/plans" className="font-semibold text-hotpink underline decoration-dotted">
                      Upgrade to Starter
                    </a>{" "}
                    to see live delivery status.
                  </p>
                </div>
              </div>
            )}
            {!isFree && decisionDialog.returnItem.kind === "exchange" && decisionDialog.decision === "approved" && (
              isOrderDelivered(decisionDialog.returnItem) ? (
                <div className="mt-2 rounded-xl bg-mint/60 px-3 py-2.5 text-xs font-semibold text-ink">
                  ✓ Order delivered — approving swaps the stock to the new size automatically.
                </div>
              ) : (
                <div className="mt-2 rounded-xl bg-peach/60 px-3 py-2.5 text-xs font-semibold text-ink">
                  ⏳ This order isn't delivered yet (in transit, not shipped, or returned to origin) — an exchange can only be approved after delivery. You can reject it, or wait.
                </div>
              )
            )}
            {decisionDialog.returnItem.kind === "exchange" && decisionDialog.decision === "approved" && (
              <div className="mt-2 flex items-start gap-2.5 rounded-xl bg-[#4C1D95]/10 px-3 py-2.5 text-xs text-ink/80">
                <Info size={14} className="mt-0.5 flex-none text-[#4C1D95]" />
                <div>
                  <p className="font-semibold text-ink">You'll need to ship the new size yourself</p>
                  <p className="mt-0.5">
                    CheckoutDM doesn't create a shipment or AWB for exchanges. After approving, book the replacement manually on your courier or shipping aggregator's website (Shiprocket, Shippo, etc.) and share the tracking details with the customer.
                  </p>
                </div>
              </div>
            )}
            {decisionDialog.returnItem.kind !== "exchange" && decisionDialog.decision === "approved" && isOrderInTransit(decisionDialog.returnItem) && (
              <div className="mt-2 rounded-xl bg-peach/60 px-3 py-2.5 text-xs font-semibold text-ink">
                ⏳ {decisionDialog.returnItem.courier_tracking_provider === "manual"
                  ? "This order shipped less than 2 days ago, so it's assumed to still be in transit. You can still approve; the customer may not have the item yet, and you'll arrange the pickup yourself."
                  : decisionDialog.returnItem.courier_tracking_provider === "shiprocket"
                  ? "This order hasn't been marked delivered yet — reverse pickup can't be scheduled until it is. You can still approve; you'll need to trigger pickup again once delivery is confirmed."
                  : "This order hasn't been marked delivered yet. Approving now still emails the customer a return label right away — they may not have the package yet."}
              </div>
            )}
            {decisionDialog.returnItem.kind !== "exchange" && decisionDialog.decision === "approved" && isOrderDelivered(decisionDialog.returnItem) && (
              <div className="mt-2 rounded-xl bg-mint/60 px-3 py-2.5 text-xs font-semibold text-ink">
                {decisionDialog.returnItem.courier_tracking_provider === "manual"
                  ? "✓ Assumed delivered (2+ days since shipping) — you'll arrange the return pickup yourself."
                  : decisionDialog.returnItem.courier_tracking_provider === "shiprocket"
                  ? "✓ Delivery confirmed — reverse pickup can be scheduled normally."
                  : "✓ Delivery confirmed — the customer will get a prepaid return label by email."}
              </div>
            )}
            {decisionDialog.returnItem.kind !== "exchange" && decisionDialog.decision === "approved" && Number(decisionDialog.returnItem.shipping_fee_charged) > 0 && (
              <div className="mt-2 rounded-xl bg-ink/[0.03] px-3 py-2.5 text-xs text-ink/70">
                <p className="font-semibold text-ink">
                  Shipping charged on this order: {formatMoney(decisionDialog.returnItem.shipping_fee_charged, currency)}
                </p>
                <p className="mt-0.5">
                  {decisionDialog.returnItem.is_partial
                    ? "Partial return — shipping is typically kept, since the rest of the order still shipped."
                    : "Full return — you may want to refund shipping along with the item(s). CheckoutDM doesn't process refunds automatically; you'll do this manually via Razorpay or your bank."}
                </p>
              </div>
            )}
            <textarea
              rows={2}
              value={merchantNote}
              onChange={(e) => setMerchantNote(e.target.value)}
              placeholder="Optional note to the customer"
              className="mt-3 w-full resize-none rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
            />
            <div className="mt-5 flex gap-3">
              <button
                onClick={() => setDecisionDialog(null)}
                disabled={isDeciding}
                className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                onClick={submitDecision}
                disabled={isDeciding}
                className={`flex-1 rounded-full py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60 ${decisionDialog.decision === "approved" ? "bg-sunset-gradient" : "bg-hotpink"}`}
              >
                {isDeciding ? "Please wait…" : `Yes, ${decisionDialog.decision === "approved" ? "approve" : "reject"}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [active]);
  return now;
}

function formatCountdown(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(totalSeconds / 60);
  const s = totalSeconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
}

function PauseResumeControl({ conversation, onPauseClick, onChanged }) {
  const pausedUntil = safeDate(conversation.human_paused_until);
  const rawIsPaused = !!pausedUntil && pausedUntil.getTime() > Date.now();
  const now = useNow(rawIsPaused);
  const isPaused = !!pausedUntil && pausedUntil.getTime() > now;
  const [resuming, setResuming] = useState(false);

  if (!isPaused) {
    return (
      <button
        onClick={() => onPauseClick(conversation)}
        className="w-full rounded-full border border-ink/15 bg-white px-2.5 py-1 text-xs font-semibold hover:bg-ink/5"
      >
        Pause Bot
      </button>
    );
  }

  const resume = async () => {
    if (resuming) return;
    setResuming(true);
    try {
      await api.post("/merchant/conversation/pause", { conversation_id: conversation.id, resume: true });
      toast.success("Bot resumed.");
      onChanged?.();
    } catch (e) {
      toast.error("Couldn't resume the bot — try again.");
    } finally {
      setResuming(false);
    }
  };

  return (
    <button
      onClick={resume}
      disabled={resuming}
      title="Bot is paused — click to resume early"
      className="w-full rounded-full bg-mint px-2.5 py-1 text-xs font-semibold text-ink disabled:opacity-50"
    >
      {resuming ? "Resuming…" : `Resume Bot (${formatCountdown(pausedUntil.getTime() - now)})`}
    </button>
  );
}

function PauseBotDialog({ conversation, open, onClose, onPaused }) {
  const PRESETS = [5, 10, 15, 30];
  const [minutes, setMinutes] = useState(10);
  const [custom, setCustom] = useState("");
  const [notify, setNotify] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  if (!open || !conversation) return null;

  const effectiveMinutes = custom ? parseInt(custom, 10) : minutes;

  const submit = async () => {
    if (!effectiveMinutes || effectiveMinutes < 1 || submitting) return;
    setSubmitting(true);
    try {
      const res = await api.post("/merchant/conversation/pause", {
        conversation_id: conversation.id,
        minutes: effectiveMinutes,
        notify_customer: notify,
      });
      toast.success(`Bot paused for ${effectiveMinutes} min${effectiveMinutes === 1 ? "" : "s"}.`);
      onPaused?.(conversation.id, res.data.human_paused_until);
      onClose();
    } catch (e) {
      toast.error("Couldn't pause the bot — try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink/40 p-4">
      <div className="w-full max-w-sm rounded-3xl bg-white p-6">
        <p className="font-display text-xl font-black tracking-tight">Pause bot for this customer</p>
        <p className="mt-2 text-sm text-ink/60">
          The bot won't reply to {conversation.display_name || "this customer"} until the pause ends — reply to them yourself on Instagram in the meantime.
        </p>

        <div className="mt-4 flex flex-wrap gap-2">
          {PRESETS.map((m) => (
            <button
              key={m}
              onClick={() => { setMinutes(m); setCustom(""); }}
              className={`rounded-full px-3 py-1.5 text-sm font-semibold ${
                !custom && minutes === m ? "bg-ink text-cream" : "border border-ink/15 bg-white"
              }`}
            >
              {m} min
            </button>
          ))}
        </div>

        <div className="mt-3">
          <label className="text-xs font-bold uppercase tracking-wide text-ink/50">Or custom (minutes)</label>
          <input
            type="number"
            min={1}
            max={1440}
            value={custom}
            onChange={(e) => setCustom(e.target.value)}
            placeholder="e.g. 45"
            className="mt-1 w-full rounded-2xl border border-ink/15 px-3 py-2 text-sm outline-none focus:border-hotpink"
          />
        </div>

        <label className="mt-4 flex items-center gap-2 text-sm">
          <input type="checkbox" checked={notify} onChange={(e) => setNotify(e.target.checked)} />
          Tell the customer the bot is paused
        </label>

        <div className="mt-5 flex gap-3">
          <button onClick={onClose} disabled={submitting} className="flex-1 rounded-full border border-ink/15 py-2.5 text-sm font-semibold disabled:opacity-40">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !effectiveMinutes || effectiveMinutes < 1}
            className="flex-1 rounded-full bg-sunset-gradient py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          >
            {submitting ? "Pausing…" : "Pause bot"}
          </button>
        </div>
      </div>
    </div>
  );
}

function StuckPanel({ merchantReady, onResolved, plan }) {
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, limit: 20 });
  const [loading, setLoading] = useState(true);
  const [pauseTarget, setPauseTarget] = useState(null);
  const isFree = plan === "free" || plan === "creator";

  useEffect(() => {
    if (!merchantReady || isFree) return;
    setLoading(true);
    cachedGet(`/merchant/stuck?page=${page}&limit=20`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [page, merchantReady, isFree]);

  if (isFree) {
    return (
      <div
        className="relative overflow-hidden rounded-[2rem] border border-ink/10 bg-white p-6 text-ink md:p-8"
        data-testid="stuck-panel-locked"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Stuck communications</p>
            <h3 className="mt-1 font-display text-2xl font-black tracking-tight">Locked on your current plan.</h3>
            <p className="mt-2 max-w-lg text-sm text-ink/60">
              Chats flagged for manual review, sitting in an active cart, pointing at a deleted product, or untouched
              for 7+ days — available from the Starter plan onwards. You're on {plan === "creator" ? "Creator" : "Free"} — upgrade to unlock.
            </p>
          </div>
          <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-ink/5 text-ink/60">
            <Lock size={18} />
          </span>
        </div>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <a
            href="/plans"
            data-testid="stuck-upgrade-cta"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
          >
            Upgrade to Starter <ArrowUpRight size={14} />
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="stuck-panel">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Stuck communications</p>
        <h2 className="mt-1 font-display text-2xl font-black tracking-tight">Needs your attention</h2>
        <p className="mt-1 text-sm text-ink/60">
          Chats flagged for manual review, sitting in an active cart, pointing at a deleted product, or untouched for 7+ days.
        </p>
      </div>

      <div className="mt-6 hidden overflow-x-auto md:block">
        <table className="w-full min-w-[760px] table-fixed text-sm">
          <colgroup>
            <col className="w-[20%]" />
            <col className="w-[16%]" />
            <col className="w-[10%]" />
            <col className="w-[28%]" />
            <col className="w-[14%]" />
            <col className="w-[12%]" />
          </colgroup>
          <thead>
            <tr className="border-b border-ink/10 text-left text-xs font-bold uppercase tracking-[0.15em] text-ink/50">
              <th className="pb-3 pr-4">Customer</th>
              <th className="pb-3 pr-4">State</th>
              <th className="pb-3 pr-4">Retries</th>
              <th className="pb-3 pr-4">Stuck on</th>
              <th className="pb-3">Last update</th>
              <th className="pb-3 pr-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="py-6 text-center text-ink/50">Loading…</td></tr>
            ) : data.items.length === 0 ? (
              <tr><td colSpan={6} className="py-6 text-center text-ink/50">Nothing stuck — nice work.</td></tr>
            ) : (
              data.items.map((c) => {
                const wantsHuman = c.manual_review_field === "support_requested";
                return (
                  <tr
                    key={c.id}
                    className={`border-b border-ink/5 ${wantsHuman ? "bg-hotpink/5" : ""}`}
                    data-testid={`stuck-row-${c.id}`}
                  >
                    <td className="py-3 pr-4 align-top">
                      <div className="flex items-center gap-2">
                        <ProfilePic src={c.profile_pic} className="h-7 w-7" iconSize={12} />
                        {c.customer_ig_handle ? (
                          <a href={igProfileUrl(c.customer_ig_handle)}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            title={`Open @${c.customer_ig_handle} on Instagram`}
                            className="font-semibold text-hotpink hover:underline"
                          >
                            {c.display_name || `ID ${c.customer_ig_id}`}
                          </a>
                        ) : (
                          <span className="font-semibold">{c.display_name || `ID ${c.customer_ig_id}`}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 pr-4 align-top"><StateBadge state={c.state} /></td>
                    <td className="py-3 pr-4 align-top font-mono">{c.retry_count || 0}</td>
                    <td className="py-3 pr-4 align-top text-ink/70">
                      {wantsHuman ? (
                        <>
                          <span className="inline-flex items-center gap-1 rounded-full bg-hotpink px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white">
                            🙋 Wants a human
                          </span>
                          {c.support_reason && (
                            <p className="mt-1.5 text-xs leading-relaxed break-words">{c.support_reason}</p>
                          )}
                        </>
                      ) : (
                        <>
                          {(c.manual_review_field || c.state || "").replace(/_/g, " ")}
                          {c.manual_review_field === "abandoned_cart" && c.abandoned_cart_reason_status === "shown" && c.abandoned_cart_reason && (
                            <p className="mt-1.5 text-xs leading-relaxed break-words text-ink/60">
                              💬 {ABANDON_REASON_LABELS[c.abandoned_cart_reason] || c.abandoned_cart_reason}
                            </p>
                          )}
                          {c.manual_review_field === "abandoned_cart" && c.abandoned_cart_reason_status === "locked" && (
                            <a href="/plans" className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-hotpink hover:underline">
                              <Lock size={10} /> Upgrade to see why
                            </a>
                          )}
                        </>
                      )}
                    </td>
                    <td className="py-3 pr-4 align-top text-ink/60">
                      <DateTimeCell value={c.updated_at} />
                    </td>
                     <td className="py-3 pr-2 align-middle">
                      <div className="flex w-full flex-col items-stretch gap-1.5">
                        <PauseResumeControl
                          conversation={c}
                          onPauseClick={setPauseTarget}
                          onChanged={() => {
                            invalidateCache("/merchant/stuck");
                            setData((d) => ({
                              ...d,
                              items: d.items.map((x) => (x.id === c.id ? { ...x, human_paused_until: null } : x)),
                            }));
                          }}
                        />
                        <button
                          onClick={async () => {
                            await api.post("/merchant/stuck/resolve", { conversation_id: c.id });
                            toast.success("Marked resolved.");
                            invalidateCache("/merchant/stuck");
                            invalidateCache("/merchant/stats");
                            onResolved?.();
                            setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== c.id) }));
                          }}
                          className="w-full rounded-full bg-ink px-2.5 py-1 text-xs font-semibold text-cream hover:bg-ink/85"
                        >
                          Resolve
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Phone layout: one card per stuck chat, no sideways scrolling */}
      <div className="mt-6 space-y-3 md:hidden">
        {loading ? (
          <p className="py-6 text-center text-ink/50">Loading…</p>
        ) : data.items.length === 0 ? (
          <p className="py-6 text-center text-ink/50">Nothing stuck — nice work.</p>
        ) : (
          data.items.map((c) => {
            const wantsHuman = c.manual_review_field === "support_requested";
            return (
              <div
                key={c.id}
                className={`rounded-2xl border border-ink/10 p-4 ${wantsHuman ? "bg-hotpink/5" : ""}`}
                data-testid={`stuck-card-${c.id}`}
              >
                <div className="flex items-center gap-3">
                  <ProfilePic src={c.profile_pic} className="h-10 w-10 flex-none" iconSize={16} />
                  <div className="min-w-0 flex-1">
                    {c.customer_ig_handle ? (
                      <a
                        href={igProfileUrl(c.customer_ig_handle)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="block truncate font-semibold text-hotpink hover:underline"
                      >
                        {c.display_name || `ID ${c.customer_ig_id}`}
                      </a>
                    ) : (
                      <p className="truncate font-semibold">{c.display_name || `ID ${c.customer_ig_id}`}</p>
                    )}
                  </div>
                  <StateBadge state={c.state} />
                </div>

                <div className="mt-3 text-xs text-ink/70">
                  {wantsHuman ? (
                    <>
                      <span className="inline-flex items-center gap-1 rounded-full bg-hotpink px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] text-white">
                        🙋 Wants a human
                      </span>
                      {c.support_reason && (
                        <p className="mt-1.5 leading-relaxed break-words">{c.support_reason}</p>
                      )}
                    </>
                  ) : (
                    <>
                      <span className="text-ink/40">Stuck on: </span>
                      {(c.manual_review_field || c.state || "").replace(/_/g, " ")}
                      {c.manual_review_field === "abandoned_cart" && c.abandoned_cart_reason_status === "shown" && c.abandoned_cart_reason && (
                        <p className="mt-1.5 leading-relaxed break-words text-ink/60">
                          💬 {ABANDON_REASON_LABELS[c.abandoned_cart_reason] || c.abandoned_cart_reason}
                        </p>
                      )}
                      {c.manual_review_field === "abandoned_cart" && c.abandoned_cart_reason_status === "locked" && (
                        <a href="/plans" className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-hotpink hover:underline">
                          <Lock size={10} /> Upgrade to see why
                        </a>
                      )}
                    </>
                  )}
                </div>

                <dl className="mt-3 grid grid-cols-2 gap-y-1.5 text-xs text-ink/70">
                  <dt className="text-ink/40">Retries</dt>
                  <dd className="text-right font-mono">{c.retry_count || 0}</dd>
                  <dt className="text-ink/40">Last update</dt>
                  <dd className="text-right"><DateTimeCell value={c.updated_at} /></dd>
                </dl>

                <div className="mt-3 flex gap-2">
                  <div className="flex-1">
                    <PauseResumeControl
                      conversation={c}
                      onPauseClick={setPauseTarget}
                      onChanged={() => {
                        invalidateCache("/merchant/stuck");
                        setData((d) => ({
                          ...d,
                          items: d.items.map((x) => (x.id === c.id ? { ...x, human_paused_until: null } : x)),
                        }));
                      }}
                    />
                  </div>
                  <button
                    onClick={async () => {
                      await api.post("/merchant/stuck/resolve", { conversation_id: c.id });
                      toast.success("Marked resolved.");
                      invalidateCache("/merchant/stuck");
                      invalidateCache("/merchant/stats");
                      onResolved?.();
                      setData((d) => ({ ...d, items: d.items.filter((x) => x.id !== c.id) }));
                    }}
                    className="flex-1 rounded-full bg-ink px-3 py-1 text-xs font-semibold text-cream hover:bg-ink/85"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      <Pager page={page} total={data.total} limit={data.limit} onPage={setPage} testPrefix="stuck" />

      <PauseBotDialog
        conversation={pauseTarget}
        open={!!pauseTarget}
        onClose={() => setPauseTarget(null)}
        onPaused={(id, humanPausedUntil) => {
          invalidateCache("/merchant/stuck");
          setData((d) => ({
            ...d,
            items: d.items.map((x) => (x.id === id ? { ...x, human_paused_until: humanPausedUntil } : x)),
          }));
        }}
      />
    </div>
  );
}

function AutomationPanel({ enabled, planName, onCreateClick }) {
  return (
    <div
      className={`relative overflow-hidden rounded-[2rem] border border-ink/10 p-6 md:p-8 ${
        enabled ? "bg-sunset-gradient text-white" : "bg-white text-ink"
      }`}
      data-testid={`automation-panel-${enabled ? "enabled" : "locked"}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${enabled ? "text-white/85" : "text-hotpink"}`}>
            Comment DM Automation
          </p>
          <h3 className="mt-1 font-display text-2xl font-black tracking-tight">
            {enabled
              ? "Turn any comment into a checkout DM."
              : "Locked on your current plan."}
          </h3>
          <p className={`mt-2 max-w-lg text-sm ${enabled ? "text-white/85" : "text-ink/60"}`}>
            {enabled
              ? "Set keywords, choose a product, and every matching comment gets a private checkout link — automatically."
              : `Comment DM automation is available on Creator, Starter, Growth & Pro. You're on ${planName || "Free"} — upgrade to unlock.`}
          </p>
        </div>
        {enabled ? (
          <Sparkles size={26} />
        ) : (
          <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-ink/5 text-ink/60">
            <Lock size={18} />
          </span>
        )}
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        {enabled ? (
          <button
            onClick={onCreateClick}
            data-testid="automation-create-rule"
            className="rounded-full bg-white/95 px-5 py-2 text-sm font-semibold text-ink hover:bg-white"
          >
            Create automation rule
          </button>
        ) : (
          <a          
            href="/plans"
            data-testid="automation-upgrade-cta"
            className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
          >
            Upgrade to Creator <ArrowUpRight size={14} />
          </a>
        )}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { me } = useMerchant();
  const owner = isOwner(me);
  const [stats, setStats] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tab, setTab] = useState("chats");
  const [statsTick, setStatsTick] = useState(0);
  const [notificationsTick, setNotificationsTick] = useState(0);

  useEffect(() => {
  if (!me) return;

  const loadSummary = (bypassCache) => {
    if (bypassCache) invalidateCache("/merchant/dashboard-summary");
    cachedGet("/merchant/dashboard-summary")
      .then((r) => {
        setStats(r.data.stats);
        setProfile(r.data.profile);
      })
      .catch(() => {});
  };

  loadSummary(false);

  // Only listen for visibilitychange — window "focus" fires for the same
  // tab-switch/alt-tab event on most browsers, so having both was silently
  // doubling every refetch (and every cache invalidation) on each return
  // to the tab. The timestamp guard below is a second safety net in case
  // a browser still fires visibilitychange more than once in quick succession.
  let lastRefetch = 0;
  const onVisible = () => {
    if (document.visibilityState !== "visible") return;
    const now = Date.now();
    if (now - lastRefetch < 2000) return;
    lastRefetch = now;
    loadSummary(true);
  };
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    document.removeEventListener("visibilitychange", onVisible);
  };
}, [me, statsTick]);

  // api-me's plan_meta is currently always {} server-side, so this can't be
  // read from me.plan_meta.features — derive it from the plan key directly,
  // matching api-config's comment_dm: true for starter/growth/pro.
  const automationEnabled = me?.plan === "creator" || me?.plan === "starter" || me?.plan === "growth" || me?.plan === "pro";
  // Story DM automation stays Growth & Pro only — unlike Comment DM
  // automation above, Starter doesn't get this one.
  const storyAutomationEnabled = me?.plan === "growth" || me?.plan === "pro";

  return (
    <div className="min-h-screen bg-cream">
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-7xl px-6 pt-10 md:px-10">
        {/* Greeting */}
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">
            Welcome back
          </p>
          <h1 className="mt-2 font-display text-4xl leading-tight tracking-tighter md:text-5xl" data-testid="dashboard-title">
            Your DM store, at a glance.
          </h1>
        </motion.div>

        {/* Stats */}
        <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-5">
          <StatCard
            label="Total chats"
            value={stats?.total_chats ?? "—"}
            icon={MessageCircle}
            accent="bg-white"
            tip="Every DM conversation ever started with your store, since day one — this never resets."
          />
          <StatCard
            label="Paid orders"
            value={stats?.paid_orders ?? "—"}
            icon={ShoppingBag}
            accent="bg-mint"
            tip="Every order marked Paid, all-time — including orders flagged for manual review after payment."
          />
          <StatCard
            label="Returns"
            value={stats?.total_returns ?? "—"}
            icon={Undo2}
            accent="bg-lilac"
            tip="Every return request ever filed, all-time — requested, approved, or rejected, all counted together."
          />
          <StatCard
            label="Stuck"
            value={stats?.stuck_chats ?? "—"}
            icon={AlertTriangle}
            accent="bg-peach"
            tip="Live count, right now — chats waiting on you: flagged for manual review, sitting in an active cart, pointing at a deleted product, or untouched for 7+ days."
          />
          <StatCard
            label="Revenue"
            value={stats ? formatMoney(stats.revenue_inr, profile?.currency) : "—"}
            icon={Wallet}
            accent="bg-ink text-cream"
            tip="Total of every paid order, all-time, in your store's set currency — a paid order in a different currency won't be included."
          />
        </div>

        <p className="mt-3 text-xs text-ink/40">
          All figures above are lifetime totals except Stuck, which reflects
          right now.{" "}
          <Link to="/status-guide" className="font-semibold text-hotpink hover:underline">
            See what every status means →
          </Link>
        </p>

        {profile && !profile.instagram_connected && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/30 bg-hotpink/5 p-5"
            data-testid="instagram-disconnected-banner"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                <AlertTriangle size={18} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Instagram disconnected</p>
                <p className="mt-0.5 text-sm text-ink/70">
                  Your bot can't send or receive DMs until you reconnect.
                </p>
              </div>
            </div>
            <button
              onClick={connectToInstagram}
              className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-sm font-semibold text-white"
              data-testid="reconnect-instagram"
            >
              Reconnect Instagram <ArrowUpRight size={14} />
            </button>
          </div>
        )}

        {owner && profile && !profile.contact_email && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-hotpink/30 bg-hotpink/5 p-5"
            data-testid="missing-email-banner"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                <AlertTriangle size={18} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Add your email</p>
                <p className="mt-0.5 text-sm text-ink/70">
                  Customers can only reach you directly if there's an email on file.
                </p>
              </div>
            </div>
            <a
              href="/profile"
              className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-center text-sm font-semibold text-white"
            >
              Add email
            </a>
          </div>
        )}

        {me?.plan === "free" && (
          <div
            className="mt-4 flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-ink/10 bg-white p-5"
            data-testid="customer-cap-banner"
          >
            <div className="flex items-center gap-3">
              <span className="grid h-11 w-11 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
                <MessageCircle size={18} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Free plan</p>
                <p className="mt-0.5 text-sm text-ink/70">
                  <strong className="text-ink">{stats?.unique_customers_this_month ?? 0}/15</strong> new customer
                  chats this month
                </p>
              </div>
            </div>
            
             <a href="/plans"
              className="cta-glow inline-flex items-center gap-2 rounded-full bg-sunset-gradient px-5 py-2 text-center text-sm font-semibold text-white"
            >
              <span>Upgrade to Starter to remove this cap</span> <ArrowUpRight size={14} className="flex-none" />
            </a>
          </div>
        )}

        <DashboardNotifications merchantReady={!!me} refreshTick={notificationsTick} />
        <FeedbackPrompt merchant={me} />

        {stats?.chats_since && (
          <p className="mt-3 text-xs text-ink/50" data-testid="chats-since">
            Chats tracked since {new Date(stats.chats_since).toLocaleDateString()}
          </p>
        )}

        {/* Automation card — setup/config entry point, owner-only */}
        {owner && (
          <div className="mt-8">
            <AutomationPanel enabled={automationEnabled} planName={me?.plan_meta?.name} onCreateClick={() => setTab("automations")} />
          </div>
        )}

        {/* Tab bar */}
        {/* Phones: tabs wrap into a 2-column grid so every tab is visible without sideways scrolling. Desktop: unchanged single pill row. */}
        <div className="mt-10 grid grid-cols-2 gap-1.5 rounded-3xl border border-ink/10 bg-white/70 p-1.5 backdrop-blur md:flex md:w-max md:items-center md:gap-2 md:rounded-full md:p-1">
          {[
            { k: "chats", label: "Chats" },
            { k: "customers", label: "Customers" },
            { k: "orders", label: "Orders" },
            ...(me?.plan === "creator" ? [] : [{ k: "shipments", label: "Shipments" }]),
            { k: "returns", label: "Returns", icon: me?.plan === "creator" ? Lock : undefined },
            { k: "stuck", label: "Stuck", icon: me?.plan === "free" || me?.plan === "creator" ? Lock : undefined },
            ...(owner ? [{ k: "automations", label: "Automations", icon: automationEnabled ? Zap : Lock }] : []),
            ...(owner ? [{ k: "story-automations", label: "Story Automations", icon: storyAutomationEnabled ? Film : Lock }] : []),
          ].map((t) => (
            <button
              key={t.k}
              onClick={() => setTab(t.k)}
              data-testid={`tab-${t.k}`}
              className={`inline-flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-center text-sm font-semibold leading-tight transition-colors md:shrink-0 md:whitespace-nowrap md:px-4 ${
                tab === t.k ? "bg-ink text-cream" : "text-ink/60 hover:text-ink"
              }`}
            >
              {t.icon && <t.icon size={12} />}
              {t.label}
            </button>
          ))}
        </div>

        <p className="mt-2 text-xs text-ink/40">
          Not sure what a tab means?{" "}
          <Link to="/tabs-guide" className="font-semibold text-hotpink hover:underline">
            See what Chats, Stuck, and every other tab tracks →
          </Link>
        </p>

        <div className="mt-6">
          {tab === "chats" && <ChatsPanel merchantReady={!!me} plan={me?.plan} />}
          {tab === "customers" && <CustomersPanel merchantReady={!!me} currency={profile?.currency} plan={me?.plan} />}
          {tab === "orders" && (
            <OrdersPanel
              merchantReady={!!me}
              currency={profile?.currency}
              returnWindowDays={profile?.return_window_days}
              plan={me?.plan}
              onNotificationsChanged={() => setNotificationsTick((t) => t + 1)}
              courierConnected={!!profile?.courier_provider}
            />
          )}
          {tab === "shipments" && <ShipmentsPanel merchantReady={!!me} plan={me?.plan} courierConnected={!!profile?.courier_provider} />}
          {tab === "returns" && me?.plan === "creator" && (
            <div className="rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8" data-testid="returns-panel-locked">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Return requests</p>
              <h3 className="mt-1 font-display text-2xl font-black tracking-tight">Not needed for digital items.</h3>
              <p className="mt-2 max-w-lg text-sm text-ink/60">
                Creator sells digital products that are delivered instantly, so returns and exchanges don't apply.
              </p>
            </div>
          )}
          {tab === "returns" && me?.plan !== "creator" && (
            <ReturnsPanel
              merchantReady={!!me}
              plan={me?.plan}
              currency={profile?.currency}
              returnWindowDays={profile?.return_window_days}
              onNotificationsChanged={() => setNotificationsTick((t) => t + 1)}
            />
          )}
          {tab === "stuck" && (
            <StuckPanel merchantReady={!!me} onResolved={() => setStatsTick((t) => t + 1)} plan={me?.plan} />
          )}
          {tab === "automations" && owner && <AutomationsPanel merchant={me} />}
          {tab === "story-automations" && owner && <StoryAutomationsPanel merchant={me} />}
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
}
